"use client";

import { ChevronDown, Command, Paperclip, Smile } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useTracking } from "@/components/widget/tracking/tracking-provider";
import {
  getMockConversation,
  getMockWelcomeMessages,
  type MockMessage,
  type MockMessageButton,
  type MockMessageMedia,
} from "@/dummy";
import { mockGiphyFetchPage } from "@/dummy/giphy";
import { AI_AGENT_ROLE, AI_ASSISTANT_NAME, resolveAiName } from "@/lib/ai-assistant";
import { isSafeExternalUrl } from "@/lib/external-url";
import {
  ALLOWED_UPLOAD_MIMES,
  fileToBase64,
  MAX_UPLOAD_BYTES,
  resolveUploadMime,
} from "@/lib/file-to-base64";
import type { GifMediaType, GiphyItem } from "@/lib/giphy";
import { ingestKindFor, useGiphy } from "@/lib/giphy-client";
import {
  useMarkVisitorChatReadMutation,
  useRemoveVisitorMediaMutation,
  useSignalVisitorTypingMutation,
  useSubmitVisitorPrechatMutation,
  useUploadVisitorMediaMutation,
  useVisitorChatHistoryQuery,
} from "@/lib/graphql/queries/generated/messaging.generated";
import { lazyChunk } from "@/lib/lazy-chunk";
import { useOutboxContext } from "@/lib/outbox-context";
import { usePreview } from "@/lib/preview/use-preview";
import { openExternalUrl, openPhone } from "@/lib/safe-open";
import {
  localSlotIdOfThreadKey,
  splitLocalKeyId,
  welcomeStateKey,
} from "@/lib/targeting/local-slots";
import { useLocalSlots } from "@/lib/targeting/local-slots-context";
import { useSessionContentContext } from "@/lib/targeting/session-content-context";
import { readSlotPick, rememberSlotPick } from "@/lib/targeting/slot-pick-store";
import {
  regularThreadSlotInput,
  type SlotPick,
  slotIdOfThreadKey,
  slotSendFields,
} from "@/lib/targeting/slot-thread";
import { useDemoContent } from "@/lib/use-demo-translator";
import { useStableCallback } from "@/lib/use-stable-callback";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";
import { useWidgetEnv } from "@/lib/use-widget-env";
import { cn } from "@/lib/utils";
import { assignmentEtaText } from "@/lib/visitor-assignment";
import { useSendVisitorMessage } from "@/lib/visitor-page/use-send-visitor-message";
import type {
  ContentButton,
  MessageSlot,
  MessageVariant,
  WidgetPrechatField,
} from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";
import { GoogleSignInButton } from "../../auth/google-sign-in-button";
import { PasskeySignInButton } from "../../auth/passkey-sign-in-button";
import { CrossFade } from "../../cross-fade";
import { AttachmentPreview } from "./composer/attachment-preview";
import { ComposerSendControls } from "./composer/composer-send-controls";
import { bindDraftToSession, draftSessionKey, loadDraftSession } from "./composer/draft-session";
import { createDraftStore, MAX_COMPOSER_CHARS } from "./composer/draft-store";
import { EmojiPicker } from "./composer/emoji-picker";
import { RichTextEditor, type RichTextEditorHandle } from "./composer/rich-text-editor";
import { hasRichFormatting, sanitizeComposerHtml } from "./composer/serialize-html";
import { useSingleFlight } from "./composer/use-single-flight";
import type { VoiceRecording } from "./composer/voice-recorder-bar";
import { loadDemoSession, rememberDemoMessage, rememberDemoSets } from "./demo-session";
import { isDemoReplyReady, pickDummyReply, pickVoiceNoteReply } from "./dummy-reply";
import { findHeaderSeed, plainText } from "./header-seed";
import { mergeHistoryAndLive } from "./merge-messages";
import { type LightboxItem, MediaLightbox } from "./message/media-lightbox";
import { MessageBubble } from "./message/message-bubble";
import { TypingBubble } from "./message/typing-bubble";
import { WelcomeMessage } from "./message/welcome-message";
import { MessageList } from "./message-list";
import { base64ToBlob, mapHistoryToMessages } from "./message-mapping";
import { buildMessageRows } from "./message-rows";
import { createOwnSendSignal } from "./own-send-signal";
import { PrechatComposerFields } from "./prechat-form/composer-fields";
import {
  collectPrechatFields,
  hasSubmittedPrechat,
  markPrechatSubmitted,
  prefillToValues,
  validatePrechat,
} from "./prechat-form/helpers";
import { resolvePrechatKnown } from "./prechat-form/known-values";
import { holdsScroll } from "./scroll-pin";
import { useReusedMessages } from "./stable-messages";
import type { AttachmentChip, MessagesTabProps } from "./types";
import { nextTypingActor, type TypingActor } from "./typing-actor";
import { findUnreadAnchor } from "./unread-anchor";
import { useCancelWhenClosed } from "./use-cancel-when-closed";
import { useInitialSlotReply } from "./use-initial-slot-reply";
import { clearsPendingReply, shouldAwaitAiReply, usePendingReply } from "./use-pending-reply";
import { useRepinOnResize } from "./use-repin-on-resize";
import { useVerifiedIdentity } from "./use-verified-identity";
import { useWelcomeContent } from "./use-welcome-content";

// Re-exported for `widget-shell`, which normalizes SSE media events
// through the same transform the history mapper uses.
export { normalizeBackendMedia, normalizeBackendSources } from "./message-mapping";

// Placeholder copy for `validatePrechat` when we only need the
// pass/fail COUNT (is the prefill complete?), not displayable messages.
const PREFILL_PROBE_MSGS = { required: "r", invalidEmail: "e", invalidPhone: "p" };

/** One welcome row: the legacy flat entry, or a picked slot variant (ids + quick replies). */
interface WelcomeEntry {
  kind: MessageSlot["kind"];
  text: string;
  slotId?: string;
  variantId?: string;
  buttons?: ContentButton[];
}

// Stable empty list for the slot hook when the config carries no `messageSlots`.
const NO_SLOTS: readonly MessageSlot[] = [];

// How long the "unread messages" divider lingers before auto-dismissing —
// a transient "here's where you left off" marker, not a permanent rule.
const DIVIDER_LINGER_MS = 4000;

// `VoiceRecorderBar` statically pulls in `wavesurfer.js` + its record
// plugin (~60 KB) for the live waveform. Most chat sessions never
// record a voice note, so lazy-load it: the chunk streams in only when
// the visitor actually taps the mic (`recording` flips true). Keeps the
// messages-tab payload — which loads on first panel open — lean.
const VoiceRecorderBar = lazyChunk(
  "voice-recorder",
  () => import("./composer/voice-recorder-bar"),
  {
    pick: (m) => m.VoiceRecorderBar,
  },
);

// The GIF/Sticker picker (Giphy) is a whole panel the visitor only opens on
// demand — lazy-split it out of the messages-tab payload, like the emoji picker
// and voice recorder.
const GiphyPicker = lazyChunk("giphy-picker", () => import("./composer/giphy-picker"), {
  pick: (m) => m.GiphyPicker,
});

/**
 * Messenger tab. Reworked Day 5 around a single rule: the input area
 * is **adaptive**:
 *
 *   1. Latest inbound message has quick-reply buttons + visitor
 *      hasn't picked one yet → the composer is REPLACED by the
 *      buttons (click-and-forget; once picked the buttons disappear
 *      everywhere). Visitor can't free-type at this stage.
 *
 *   2. Otherwise → a textarea + action toolbar. The toolbar's send
 *      button morphs:
 *        - empty draft + no attachments     → microphone (record)
 *        - non-empty / has attachments      → paper plane (send)
 *        - actively recording               → red square (stop)
 *      One button, three states, one tap surface.
 *
 *   3. **Demo mode** allows sending — visitor message appends
 *      immediately, a canned AI reply lands ~900 ms later so the
 *      surface feels alive even with no BE behind it.
 *
 * Motion: bubbles enter with stagger fade; typing dots animate;
 * launcher hover for icons via `lucide-animated`.
 */
/** The `threadKey` / `messageSlot` input fields of a direct (non-outbox) send. */
function slotRouting(f: ReturnType<typeof slotSendFields>) {
  return {
    ...(f.threadKey ? { threadKey: f.threadKey } : {}),
    ...(f.messageSlot ? { messageSlot: f.messageSlot } : {}),
  };
}

export function MessagesTab({
  isDummy,
  chatId,
  threadKey,
  onChatIdResolved,
  token,
  prechatForm,
  messenger,
  organizationLogoUrl,
  organizationName,
  operatorAvatars,
  catchUpHistory = false,
  isOpen = true,
  assignmentStatus,
  assignmentEstimate,
  threadStatus,
  blocked,
  globalBlocked,
  onStartNewConversation,
  onInbound,
  onOutbound,
  registerRealtimeHandler,
  unreadCount = 0,
  clearUnread,
  liveMessages: liveMessagesProp,
  appendLiveMessage,
  replaceLiveMessage,
  enqueueSend: enqueueSendProp,
  onRetryOutbox,
  initialVisitorMessage,
  onInitialVisitorMessageSent,
  welcomeSlotId,
  visitorIdentity,
  visitorPrefill,
  security,
  verified = false,
  identified: identifiedProp,
  sessionName,
  onGoogleCredential,
  onPasskeySession,
  onInternalLink,
  getTurnstileToken,
}: MessagesTabProps) {
  const t = useTranslations("messages");
  const tDemo = useDemoContent();
  const tPrechat = useTranslations("prechat");
  const locale = useLocale();
  const { trackLinkClick, trackButtonClick } = useTracking();
  const env = useWidgetEnv();
  // Resolved AI agent name (operator-configurable; null when the agent is
  // disabled) — passed to the bubbles so AI messages read as the configured
  // name + "AI Agent" role instead of the hardcoded default.
  const aiName = resolveAiName(messenger?.aiAssistant);
  // Local "thinking" bubble from the visitor's send until the first real frame (see use-pending-reply).
  const outboxOnline = useOutboxContext()?.online;
  const pendingReply = usePendingReply(
    shouldAwaitAiReply({
      aiEnabled: messenger?.aiAssistant?.enabled,
      assignmentStatus,
      online: outboxOnline,
    }),
  );

  // Per-integration scope for the prechat-dedup localStorage key. The
  // visitor config no longer carries an integrationId — the publicKey the
  // widget already holds is the stable per-integration id. Read at render
  // (the provider stamps it before this tab ever mounts).
  const instance = useWidgetInstance();
  const prechatScope = instance.publicKey ?? "";

  const [prechatDone, setPrechatDone] = useState<boolean | null>(null);
  useEffect(() => {
    setPrechatDone(hasSubmittedPrechat(prechatScope, instance.storage));
  }, [prechatScope, instance.storage]);

  // Prechat values + per-field errors live here (not in a child form)
  // because the prechat now sits ABOVE the editor and submits together
  // with the first message — `handleSend` orchestrates both.
  const [prechatValues, setPrechatValues] = useState<Record<string, string>>({});
  const [prechatErrors, setPrechatErrors] = useState<Record<string, string>>({});
  const [prechatSubmitError, setPrechatSubmitError] = useState<string | null>(null);
  const [submitPrechat, { loading: submittingPrechat }] = useSubmitVisitorPrechatMutation();
  // The prechat name feeds `contact.first_name` in the session content (contract v1).
  const { refresh: refreshSessionContent } = useSessionContentContext();

  const setPrechatField = useCallback((key: string, value: string) => {
    setPrechatValues((prev) => ({ ...prev, [key]: value }));
    setPrechatErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Host-supplied default profile mapped onto the form's field keys
  // (NAME→name, EMAIL→email, PHONE→phone). Drives both the pre-fill and
  // the auto-attach below. `{}` when nothing was passed.
  const prefillValues = useMemo<Record<string, string>>(
    () => (prechatForm ? prefillToValues(prechatForm, visitorPrefill) : {}),
    [prechatForm, visitorPrefill],
  );

  // Seed the editable fields from the prefill — but never clobber what
  // the visitor already typed (only fill empties). Re-runs if the prefill
  // arrives late (the iframe receives it over postMessage after mount).
  useEffect(() => {
    if (Object.keys(prefillValues).length === 0) return;
    setPrechatValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [k, v] of Object.entries(prefillValues)) {
        if (!next[k]) {
          next[k] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [prefillValues]);

  // Did the behind-the-scenes prefill attach fail? Then we fall back to
  // showing the (pre-filled) form so the visitor can submit it manually.
  const [prefillAttachFailed, setPrefillAttachFailed] = useState(false);

  // An IDENTIFIED visitor (host identity, a verified / signed-in session, a signed-in preview) already
  // has name / email / phone on their People row: the pre-chat only asks for the REQUIRED fields that
  // are still unknown (a required CUSTOM_TEXT field the profile does not hold still shows), and what is
  // known is prefilled, read-only when it comes from a verified source. Sources: the host identity
  // prop, the BE profile (`useVerifiedIdentity`; it only returns the fields the operator allows in
  // `messenger.profile.fields`), and the session's display name. Anonymous visitors: unchanged.
  const identified = identifiedProp ?? verified;
  const previewActive = usePreview().token !== null;
  const knownMode = identified || !!visitorIdentity;
  const {
    identity: profileIdentity,
    fieldValues: profileValues,
    pending: profileIdentityPending,
  } = useVerifiedIdentity({
    verified: knownMode,
    enabled: !!prechatForm?.enabled && !isDummy && !!token,
  });
  const hostVerified = !!(
    visitorIdentity?.jwt ||
    visitorIdentity?.googleIdToken ||
    (visitorIdentity?.userId && visitorIdentity?.userHash)
  );
  const known = useMemo(
    () =>
      resolvePrechatKnown({
        form: prechatForm ?? null,
        active: knownMode,
        host: visitorIdentity,
        hostVerified,
        profile: profileIdentity,
        profileCustom: profileValues,
        session: { name: sessionName },
        // The signed-in preview operator is the verified identity itself.
        lockServerSide: previewActive && identified,
      }),
    [
      prechatForm,
      knownMode,
      visitorIdentity,
      hostVerified,
      profileIdentity,
      profileValues,
      sessionName,
      previewActive,
      identified,
    ],
  );
  const prechatCoveredByIdentity = known.covered;
  // What the form shows: known values under what the visitor typed, locked values always on top.
  const formValues = useMemo(() => {
    const out: Record<string, string> = { ...known.values, ...prechatValues };
    for (const k of known.locked) out[k] = known.values[k] ?? "";
    return out;
  }, [known, prechatValues]);

  // Does the host-supplied prefill cover every REQUIRED field (with valid
  // formats)? Then we attach it behind the scenes + skip the form. Needs
  // at least one passed value (so an all-optional form isn't auto-skipped
  // on empty prefill). `validatePrechat` returning no errors == every
  // required field present + email/phone formats valid.
  const prechatSatisfiedByPrefill = useMemo(() => {
    if (!prechatForm?.enabled || prechatForm.fields.length === 0) return false;
    if (Object.keys(prefillValues).length === 0) return false;
    return (
      Object.keys(validatePrechat(prechatForm, prefillValues, PREFILL_PROBE_MSGS)).length === 0
    );
  }, [prechatForm, prefillValues]);

  // Attach the prefill behind the scenes once auth is ready, then mark the
  // prechat done so the form never shows. On failure, fall back to the
  // (pre-filled) form for a manual submit. Skipped when a verified
  // identity already wrote the profile (its handshake covers it).
  const prefillAttemptedRef = useRef(false);
  useEffect(() => {
    if (!prechatSatisfiedByPrefill || prechatDone !== false) return;
    if (prefillAttachFailed || prefillAttemptedRef.current) return;
    if (prechatCoveredByIdentity || !prechatForm) return;
    if (!isDummy && !token) return; // the real submit needs the visitor JWT
    prefillAttemptedRef.current = true;
    (async () => {
      if (isDummy) {
        markPrechatSubmitted(prechatScope, instance.storage);
        setPrechatDone(true);
        return;
      }
      try {
        const turnstileToken = (await getTurnstileToken?.()) ?? undefined;
        await submitPrechat({
          variables: {
            input: { fields: collectPrechatFields(prechatForm, prefillValues), turnstileToken },
          },
        });
        markPrechatSubmitted(prechatScope, instance.storage);
        setPrechatDone(true);
        refreshSessionContent();
      } catch {
        prefillAttemptedRef.current = false;
        setPrefillAttachFailed(true);
      }
    })();
  }, [
    prechatSatisfiedByPrefill,
    prechatDone,
    prefillAttachFailed,
    prechatCoveredByIdentity,
    prechatForm,
    prefillValues,
    isDummy,
    token,
    prechatScope,
    instance.storage,
    submitPrechat,
    getTurnstileToken,
    refreshSessionContent,
  ]);

  // Prechat shows when the operator configured it, the visitor hasn't
  // submitted, a known-user identity doesn't already cover the required
  // fields, AND a host prefill isn't covering them (unless that attach
  // failed — then show the pre-filled form). The compact fields render
  // above the editor; the single send submits them with the first message.
  const showPrechat =
    !!prechatForm?.enabled &&
    prechatForm.fields.length > 0 &&
    prechatDone === false &&
    !profileIdentityPending &&
    !prechatCoveredByIdentity &&
    !(prechatSatisfiedByPrefill && !prefillAttachFailed);

  // Auth gate — when the operator requires a verified identity and the
  // visitor isn't yet verified, the composer is replaced by the sign-in
  // panel (they must authenticate before they can message).
  //
  // In DEMO/preview there's no real auth backend (and `verified` never
  // flips), so we render a PREVIEW gate with mock buttons that
  // fake-complete via `demoAuthed` — letting operators eyeball the
  // gate → composer transition. In production the real Google / passkey
  // buttons drive the actual handshake (which flips `verified`).
  // Persisted to sessionStorage so a demo/preview RELOAD keeps the
  // fake-signed-in state (the demo has no real session to restore — see
  // `useVisitorAuth`'s localStorage persistence for the production path).
  const [demoAuthed, setDemoAuthedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return instance.sessionStorage.getItem("wexio:demo-authed") === "1";
    } catch {
      return false;
    }
  });
  const setDemoAuthed = (value: boolean) => {
    setDemoAuthedState(value);
    try {
      if (value) instance.sessionStorage.setItem("wexio:demo-authed", "1");
      else instance.sessionStorage.removeItem("wexio:demo-authed");
    } catch {
      /* private mode — preview just won't survive reload */
    }
  };
  // Resolved Google clientId — non-null only when the operator both
  // enabled Google AND configured a clientId. The widget runs GIS
  // directly with it (the BE verifies `id_token.aud` server-side so
  // the public clientId is safe on the visitor surface).
  const googleSignIn = security?.google?.enabled ? security.google.clientId : null;
  const passkeySignIn = !!security?.passkey?.enabled;
  // `demoAuthed` only ever suppresses the gate in DEMO mode — in
  // production the gate follows the real `verified` flag, so a stale
  // demo flag (same-tab sessionStorage) can't hide a real sign-in gate.
  // A BE preview (BE 062) has ONE auth path (`startAnonymousVisitor`): the gate shows the same mock
  // buttons as demo and no real Google / passkey / OTP ceremony ever starts.
  const mockAuth = isDummy || previewActive;
  const showAuthGate = !!security?.requireAuth && !identified && !(mockAuth && demoAuthed);
  // A CLOSED conversation locks the composer entirely — the visitor sees a
  // "conversation closed" notice + a "start a new conversation" button instead.
  const isClosed = threadStatus === "CLOSED";
  // BLOCKED locks the composer too and takes PRIORITY over CLOSED. A thread-only
  // block still lets the visitor start a new conversation; a global (contact)
  // block hides that action as well.
  const isBlocked = !!blocked;
  const isGloballyBlocked = !!globalBlocked;

  // Gate any outbound action (text send OR voice note) behind the
  // prechat: validate the fields, then submit them so the People
  // profile is attached before the message. Returns `false` (and shows
  // errors) when the fields are missing/invalid or the submit fails, so
  // the caller aborts. A no-op (`true`) when prechat isn't required —
  // this is what stops a voice recording from bypassing the form.
  const ensurePrechat = useCallback(async (): Promise<boolean> => {
    if (!showPrechat || !prechatForm) return true;
    const errs = validatePrechat(prechatForm, formValues, {
      required: tPrechat("required"),
      invalidEmail: tPrechat("invalidEmail"),
      invalidPhone: tPrechat("invalidPhone"),
    });
    if (Object.keys(errs).length > 0) {
      setPrechatErrors(errs);
      return false;
    }
    setPrechatSubmitError(null);
    if (isDummy) {
      // Dummy / preview — no auth'd visitor, so skip the BE mutation
      // and complete locally.
      markPrechatSubmitted(prechatScope, instance.storage);
      setPrechatDone(true);
      return true;
    }
    try {
      const turnstileToken = (await getTurnstileToken?.()) ?? undefined;
      await submitPrechat({
        variables: {
          input: { fields: collectPrechatFields(prechatForm, formValues), turnstileToken },
        },
      });
      markPrechatSubmitted(prechatScope, instance.storage);
      setPrechatDone(true);
      refreshSessionContent();
      return true;
    } catch (err) {
      setPrechatSubmitError(err instanceof Error ? err.message : tPrechat("submitFailed"));
      return false;
    }
  }, [
    showPrechat,
    prechatForm,
    formValues,
    isDummy,
    prechatScope,
    instance.storage,
    submitPrechat,
    tPrechat,
    getTurnstileToken,
    refreshSessionContent,
  ]);

  // The draft's `text` is the composer's PLAIN-TEXT projection (drives empty-state →
  // mic↔send, the typing signal, and the send-empty guard); its `html` is the
  // live rich-text HTML the editor holds, sanitised on send. The editor owns
  // its own DOM value — we drive it imperatively (emoji insert / clear) via
  // `editorHandleRef` rather than as a controlled `value`.
  // Lives outside React state: a keystroke must not re-render the thread.
  // Unsent text survives leaving the thread (the tab remounts per route): restored from, and
  // mirrored to, the per-thread session map.
  const demoSessionKey = threadKey ?? "main";
  const draftKey = draftSessionKey(instance.publicKey, threadKey ?? null);
  const [draftStore] = useState(() =>
    createDraftStore(loadDraftSession(draftKey, instance.drafts)),
  );
  useEffect(
    () => bindDraftToSession(draftStore, draftKey, instance.drafts),
    [draftStore, draftKey, instance.drafts],
  );
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [giphyOpen, setGiphyOpen] = useState(false);
  // Show the "scroll to newest" button when the visitor has scrolled up away
  // from the bottom (set from the scroll handler, which already tracks it).
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Welcome reveal: on a NEW thread's first open, show a typing indicator (~3s)
  // before the welcome MESSAGE bubbles appear. `null` = UNDECIDED (render
  // NOTHING until the layout-effect below picks typing vs bubbles) — otherwise
  // the first frame renders the bubble, and the `AnimatePresence mode="wait"`
  // swap to typing plays the bubble's EXIT animation, which reads as a
  // "bubble -> dots -> bubble" blink. Starting from nothing means there's no
  // bubble to animate out.
  const [welcomeTyping, setWelcomeTyping] = useState<boolean | null>(null);
  // Threads (by key) whose welcome reveal has already played — so it animates
  // once per new thread, not on every re-render / re-open.
  const welcomeShownRef = useRef<Set<string>>(new Set());
  // When each thread's typing beat started (see the reveal effect below).
  const welcomeTypingStartRef = useRef<Map<string, number>>(new Map());
  const [recording, setRecording] = useState(false);
  // The tab stays mounted while the panel is closed: stop a voice recording (release the mic).
  useCancelWhenClosed(isOpen, () => setRecording(false));
  const editorHandleRef = useRef<RichTextEditorHandle | null>(null);
  const emojiAnchorRef = useRef<HTMLButtonElement | null>(null);
  const giphyAnchorRef = useRef<HTMLButtonElement | null>(null);
  const commandsAnchorRef = useRef<HTMLButtonElement | null>(null);
  // Close the commands dropdown on outside click. Lightweight
  // mirror of the EmojiPicker's click-outside handling so the
  // dropdown behaves naturally without its own portal.
  useEffect(() => {
    if (!commandsOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      // composedPath (not e.target): in the Shadow-DOM embed a document listener
      // sees the event retargeted to the shadow host, so contains()/closest()
      // read every in-dropdown click as "outside". composedPath pierces the
      // shadow boundary (and is the ordinary path in light DOM).
      const path = e.composedPath();
      if (commandsAnchorRef.current && path.includes(commandsAnchorRef.current)) return;
      // The dropdown's panel carries this data attribute — see the
      // JSX below — so clicks inside it don't close.
      if (path.some((n) => n instanceof Element && n.matches("[data-wx-commands-panel]"))) {
        return;
      }
      setCommandsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [commandsOpen]);

  // Insert the emoji at the caret via the editor's imperative handle; it
  // manages its own contentEditable selection (the shell can't reach into it).
  const insertEmoji = (emoji: string) => {
    editorHandleRef.current?.insertText(emoji);
  };

  // Clear the composer after a send (both projections + the editor's DOM).
  const clearComposer = useCallback(() => {
    draftStore.set("", "");
    editorHandleRef.current?.clear();
  }, [draftStore]);

  // The contentEditable editor grows itself (min/max-height + overflow), so no
  // measure effect is needed the way the old textarea required.
  // Live list. In demo mode we keep the getMockConversation() seed local
  // (the shell doesn't seed it). In real mode the shell owns the
  // session-durable list and feeds it via `liveMessagesProp`; the
  // local fallback below covers the rare case where the prop is
  // omitted (e.g. tab rendered outside the widget shell in tests).
  // Demo seed: the anchor/main thread (no `threadKey`) shows the full mock
  // conversation (the media showcase); a NEW-question / topic thread (a
  // `threadKey` is set) starts EMPTY so it shows only the welcome greeting —
  // "new chat" should be clean. The tab remounts per thread (`routeKey`), so
  // this initializer re-runs with the right `threadKey`.
  //
  // The seed is DERIVED from `tDemo`, not captured by a `useState` initializer:
  // `useDemoContent()` returns an EMPTY translator until the async demo bundle
  // lands, so an initializer froze every text / button label / media alt blank
  // (empty quick-reply pills, `application/pdf` instead of a file name) and
  // never refreshed. State holds only what the visitor appends on top.
  const demoSeed = useMemo(
    () => (isDummy && !threadKey ? getMockConversation(tDemo) : []),
    [isDummy, threadKey, tDemo],
  );
  // Kept for the page's lifetime (see demo-session): leaving and re-entering the thread must not
  // drop the visitor's exchange.
  const [demoAppended, setDemoAppended] = useState<MockMessage[]>(
    () => loadDemoSession(demoSessionKey).appended,
  );
  const demoMessages = useMemo(() => [...demoSeed, ...demoAppended], [demoSeed, demoAppended]);
  const liveMessages = isDummy ? demoMessages : (liveMessagesProp ?? []);
  // Wrapper that routes appends to the right backing store —
  // `appendLiveMessage` (shell) for real mode, local `setDemoAppended`
  // for demo. Dedupes by id either way.
  // The scroll engine scrolls a scrolled-up reader to a new bubble ONLY for their own send; this is
  // the explicit signal (older history prepending also grows the list).
  const [ownSend] = useState(() => createOwnSendSignal());
  // The variant this slot thread shows (`message:<slotId>` threads only); set once the welcome
  // content resolves below. Every send into the thread carries `messageSlot`: the pick the thread was
  // seeded with (remembered from its first send, so it survives the slot leaving the projection), else
  // the visible one. A new thread with no visible variant is not seeded: it goes to the main chat.
  const slotPickRef = useRef<SlotPick | null>(null);
  // The variant a regular thread started on a page shows (its first send asks the BE to seed it too).
  const welcomePickRef = useRef<SlotPick | null>(null);
  // A LOCAL slot thread (`slot-<id>`): the header it shows; the first send freezes it as a snapshot.
  const localHeaderRef = useRef<{ slotId: string; variant: MessageVariant } | null>(null);
  const localSlots = useLocalSlots();
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const slotSend = useCallback(() => {
    const slotId = slotIdOfThreadKey(threadKey);
    const fields = slotSendFields({
      threadKey,
      current: slotPickRef.current,
      remembered: slotId ? readSlotPick(instance.storage, instance.publicKey ?? "", slotId) : null,
      locale,
      exists: !!chatIdRef.current,
    });
    if (fields.remember)
      rememberSlotPick(instance.storage, instance.publicKey ?? "", fields.remember);
    if (slotId) return fields;
    // A local slot thread sends no `messageSlot` (the BE does not know the slot): its header is
    // persisted client-side instead, once.
    const header = localHeaderRef.current;
    const localId = localSlotIdOfThreadKey(threadKey);
    if (header && localId) {
      // A variant thread (`slot-<slot>:<variant>`) keeps its own snapshot.
      if (!localSlots.snapshotOf(localId))
        localSlots.remember(header.slotId, header.variant, !!splitLocalKeyId(localId).variantKeyId);
      return fields;
    }
    const messageSlot = regularThreadSlotInput({
      threadKey,
      pick: welcomePickRef.current,
      exists: !!chatIdRef.current,
      locale,
    });
    return messageSlot ? { ...fields, messageSlot } : fields;
  }, [threadKey, locale, instance, localSlots]);
  const enqueueSend = useMemo<typeof enqueueSendProp>(
    () =>
      enqueueSendProp
        ? (payload) => {
            ownSend.mark();
            pendingReply.arm();
            const { threadKey: key, messageSlot } = slotSend();
            return enqueueSendProp({
              ...payload,
              threadKey: key,
              ...(messageSlot ? { messageSlot } : {}),
            });
          }
        : undefined,
    [enqueueSendProp, ownSend, slotSend, pendingReply.arm],
  );
  const appendLive = useCallback(
    (msg: MockMessage) => {
      if (msg.sender?.kind === "visitor") {
        ownSend.mark();
        if (!isDummy) pendingReply.arm();
      }
      if (isDummy) {
        rememberDemoMessage(demoSessionKey, msg);
        setDemoAppended((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        return;
      }
      appendLiveMessage?.(msg);
    },
    [isDummy, appendLiveMessage, demoSessionKey, ownSend, pendingReply.arm],
  );

  // GIF / Sticker picker (Giphy). Demo mode uses a local mock so the picker is
  // reviewable without the BE; production hits our BE proxy + re-host ingest.
  const { fetchPage: liveGiphyFetch } = useGiphy();
  const giphyFetchPage = isDummy ? mockGiphyFetchPage : liveGiphyFetch;
  const handleGiphyPick = useCallback(
    (item: GiphyItem, type: GifMediaType) => {
      if (isDummy) {
        // No BE in demo — append the picked asset straight to the local thread.
        appendLive({
          id: `gif-${item.id}-${Date.now()}`,
          direction: "INBOUND",
          text: "",
          media: [{ url: item.sourceUrl, mimetype: item.mimetype, alt: item.title }],
          sender: { kind: "visitor", name: "" },
          ageLabel: new Date().toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" }),
          deliveryStatus: "SENT",
        });
        return;
      }
      if (!enqueueSend) return;
      // Enqueue immediately as PENDING Giphy media — the bubble appears at once
      // from `previewUrl`, and the shell's outbox `run` re-hosts it
      // (uploadVisitorMediaFromUrl) + sends in the background (with retry). No
      // await here, so there's no wait between tap and bubble.
      onOutbound?.();
      enqueueSend({
        text: "",
        threadKey: threadKey ?? null,
        chatId: chatId ?? null,
        media: [
          {
            filename: item.title || "gif",
            mimetype: item.mimetype || "image/gif",
            giphyUrl: item.sourceUrl,
            gifKind: ingestKindFor(type),
            previewUrl: item.previewUrl,
            previewWidth: item.previewWidth,
            previewHeight: item.previewHeight,
          },
        ],
      });
    },
    [isDummy, appendLive, enqueueSend, onOutbound, threadKey, chatId, locale],
  );

  const [consumedButtonIds, setConsumedButtonIds] = useState<Set<string>>(
    () => new Set(isDummy ? loadDemoSession(demoSessionKey).consumed : []),
  );
  const [typingActor, setTypingActor] = useState<TypingActor | null>(null);
  // A real typing beat replaces the local guess in the same render (no gap).
  const shownTyping = useMemo<TypingActor | null>(
    () =>
      typingActor ??
      (pendingReply.pending
        ? { name: aiName ?? AI_AGENT_ROLE, kind: "ai", phase: "thinking" }
        : null),
    [typingActor, pendingReply.pending, aiName],
  );
  // Auto-dismiss timer for the typing bubble. Held in a ref so each new
  // "typing" beat REPLACES the prior timer instead of stacking, and so a
  // real message can cancel it immediately. Cleared on unmount.
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    },
    [],
  );
  const [attachments, setAttachments] = useState<AttachmentChip[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadFile] = useUploadVisitorMediaMutation();
  const [removeUploadedMedia] = useRemoveVisitorMediaMutation();
  const [markChatRead] = useMarkVisitorChatReadMutation();
  const outbox = useOutboxContext();
  // Tempids of chips the visitor removed while the upload was still in flight
  // — when the upload resolves, we clean up the orphan on the BE.
  const removedDuringUploadRef = useRef<Set<string>>(new Set());

  // Visitor's scroll position inside the conversation. Drives both
  // auto-scroll-on-new-message (only if they were already at the
  // bottom — don't yank them away from history they're reading) and
  // the read-ack decision (only mark messages as read when the new
  // bubble is actually on screen). The value lives only on a ref now —
  // the read-ack listener consults live scroll geometry on every event
  // rather than gating on a state flag, and the auto-scroll branch
  // reads `atBottomRef.current` directly. Removing the React state
  // avoided a re-render on every threshold crossing.
  const atBottomRef = useRef(true);
  // "Pin to bottom" permission. Goes FALSE when an operator/AI message lands
  // while the visitor was at the bottom — they should stay where they were,
  // not get yanked to the new bottom (long messages must be read from the
  // top). Goes TRUE again only when the visitor actually scrolls to the
  // bottom (acknowledging the new content) or sends their own message.
  // Gates EVERY ambient bottom-scroll path (typing-beat auto-scroll AND the
  // image-load re-pin) so a single source of truth controls the lock.
  const bottomPinAllowedRef = useRef(true);
  // Deadline (ms epoch) until which a media load re-pins to the bottom even though a scroll
  // event has not yet confirmed we are there: set when we glide to the bottom ourselves, cleared
  // the moment the visitor scrolls. Without it an image landing MID-glide (`atBottomRef` still
  // false) grew the thread and the glide ended short of the bottom.
  const settleToBottomUntilRef = useRef(0);

  // Guards against firing `markChatRead` twice for the same ack
  // window. Released when the unread count drops to 0 (either via
  // the BE's read-receipt fanout or our local `clearUnread`).
  const ackingRef = useRef(false);
  useEffect(() => {
    if (unreadCount === 0) ackingRef.current = false;
  }, [unreadCount]);

  const ackRead = useCallback(() => {
    if (ackingRef.current) return;
    if (unreadCount === 0) return;
    ackingRef.current = true;
    // Optimistic local clear so the launcher badge / peek vanish
    // immediately. If the BE mutation fails the next page-load's
    // initial `visitorChatUnread` will reconcile.
    clearUnread?.();
    // Demo mode has no chat to ack against — the local clear above is
    // the whole story, so the launcher peek doesn't resurface after the
    // visitor opens the conversation and closes the widget again.
    if (isDummy || !chatId) return;
    // READ boundary — which chatId are we clearing? (focused topic thread vs
    // main/legacy). Prime suspect for "unread doesn't clear" is a mismatch here.
    // Offline outbox path — queue a CONVERGENCE read-ack (per chat) so it syncs
    // + retries on reconnect; a later read on the same chat supersedes it. The
    // local clear above already updated the UI. Falls back to a direct mutation
    // when the queue is off.
    if (outbox) {
      outbox.enqueue({
        op: "readAck",
        semantics: "convergence",
        convergenceKey: `read:${chatId}`,
        payload: { chatId },
      });
      return;
    }
    markChatRead({ variables: { chatId } }).catch(() => {
      ackingRef.current = false;
    });
  }, [chatId, isDummy, unreadCount, markChatRead, clearUnread, outbox]);

  // SSE read-receipts upgrade bubbles in realtime — we hold the
  // message ids the BE flipped in a Set, and the bubble renderer
  // forces deliveryStatus="READ" for any id present here. Avoids
  // mutating Apollo's chat-history cache for an in-conversation
  // overlay state.
  const [readMessageIds, setReadMessageIds] = useState<Set<string>>(
    () => new Set(isDummy ? loadDemoSession(demoSessionKey).read : []),
  );
  useEffect(() => {
    if (isDummy) rememberDemoSets(demoSessionKey, consumedButtonIds, readMessageIds);
  }, [isDummy, demoSessionKey, consumedButtonIds, readMessageIds]);

  // Lightbox state lives at the tab level — any bubble's image/video
  // tap opens the same overlay. `items` carries just the tapped
  // bubble's media (one gallery per message) so navigation stays
  // scoped to that message's batch.
  const [lightbox, setLightbox] = useState<{
    items: LightboxItem[];
    index: number;
  } | null>(null);
  const openLightbox = (items: LightboxItem[], index: number) => setLightbox({ items, index });

  // History fetch — only real mode. `refetch()` is used after the
  // visitor sends so Apollo's cache picks up the BE-persisted row
  // (real id, server-stamped createdAt + deliveryStatus). Without
  // this, a Messages → Help → Messages round-trip after a send
  // would serve the stale pre-send cache and lose the bubble.
  const {
    data: historyData,
    loading: historyLoading,
    fetchMore: fetchMoreHistory,
    refetch: refetchHistory,
  } = useVisitorChatHistoryQuery({
    // Initial page is 10 messages — enough to fill the panel without
    // a wall of old chat scrolling under the visitor on first open.
    // Older messages stream in as the visitor scrolls up; see the
    // `loadOlder` handler below.
    variables: { chatId: chatId ?? "", limit: 10 },
    skip: isDummy || !chatId || !token,
    // Fresh-on-mount: in-session messages (optimistic sends + SSE inbound)
    // live only in the shell's `liveMessages`, which is wiped when the
    // widget unmounts. On an SPA re-navigation back to the widget the tab
    // remounts with an empty live buffer, so the history query MUST hit the
    // network — the default `cache-first` (see apollo-client.ts) would serve
    // the stale pre-session page and the session's messages would vanish
    // until a full reload. `cache-and-network` paints the cache instantly
    // then reconciles from the BE; `nextFetchPolicy: "cache-first"` keeps
    // `fetchMore` pagination on the cache (no per-scroll network refetch).
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  // Catch-up refetch for a JUST-adopted lazy chat (`catchUpHistory`): on the
  // "" -> real chatId swap the history query re-fires (its chatId variable
  // changed), and that fetch can race the FIRST_VISIT flow's greeting — the flow
  // publishes it to the new chat's SSE channel the instant the chat is created,
  // which can be BEFORE the per-chat stream has subscribed, so the live frame is
  // missed. One extra network refetch shortly after adoption pulls that greeting
  // into the thread; the SSE covers everything after. (The tab does NOT remount
  // on adoption — `routeKey` keys off the stable thread identity, not chatId —
  // so this effect fires on the prop/chatId change, not on mount.)
  useEffect(() => {
    if (!catchUpHistory || isDummy || !chatId || !token) return;
    const id = setTimeout(() => {
      void refetchHistory?.().catch(() => {});
    }, 700);
    return () => clearTimeout(id);
  }, [catchUpHistory, isDummy, chatId, token, refetchHistory]);

  // Pagination cursor — the id of the OLDEST currently-loaded
  // message. The BE's `visitorChatHistory(before: …)` returns
  // messages strictly older than the cursor, so we use the last
  // entry of the raw (newest-first) array. `null` while history
  // is empty or in flight; the scroll-up handler short-circuits.
  const oldestMessageId: string | null = (() => {
    const rawList = (historyData?.visitorChatHistory as { items?: unknown } | undefined)?.items;
    if (!Array.isArray(rawList) || rawList.length === 0) return null;
    const last = rawList[rawList.length - 1] as Record<string, unknown>;
    return typeof last?.id === "string" ? last.id : null;
  })();
  // Whether the BE says there's an OLDER page. `nextCursor: null` means the
  // current page was the last one — so we must NOT fire a `before` fetch
  // even though the scroll-up handler triggers on the initial layout (a
  // short thread sits at `scrollTop = 0`). Without this gate, opening a
  // chat with fewer than `limit` messages fired a redundant empty page.
  const historyHasMore = !!(
    historyData?.visitorChatHistory as { nextCursor?: string | null } | undefined
  )?.nextCursor;
  // Track when older-page fetches are in flight so the visitor sees
  // a skeleton strip at the top of the list (and we don't fire
  // overlapping requests if they scroll fast).
  const [loadingOlder, setLoadingOlder] = useState(false);
  // `true` once a fetchMore returns no items — stops further
  // upward-scroll requests so we don't paper the BE with empty
  // pagination calls.
  const [reachedHistoryStart, setReachedHistoryStart] = useState(false);

  const loadOlder = useCallback(async () => {
    if (
      isDummy ||
      !chatId ||
      !oldestMessageId ||
      loadingOlder ||
      reachedHistoryStart ||
      !historyHasMore
    ) {
      return;
    }
    setLoadingOlder(true);
    try {
      const res = await fetchMoreHistory({
        variables: { chatId, before: oldestMessageId, limit: 10 },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const prevList = ((prev.visitorChatHistory as { items?: unknown[] } | undefined)?.items ??
            []) as Record<string, unknown>[];
          const moreList = ((
            fetchMoreResult.visitorChatHistory as { items?: unknown[] } | undefined
          )?.items ?? []) as Record<string, unknown>[];
          // Dedup by id. Same reasoning as the news-tab fetchMore
          // dedup — page boundary races + StrictMode double-mount
          // both lead to overlapping items.
          const seen = new Set(
            prevList
              .map((m) => (typeof m.id === "string" ? m.id : null))
              .filter((id): id is string => !!id),
          );
          const merged = [
            ...prevList,
            ...moreList.filter((m) => typeof m.id !== "string" || !seen.has(m.id)),
          ];
          return {
            ...prev,
            visitorChatHistory: {
              ...(fetchMoreResult.visitorChatHistory as object),
              items: merged,
            },
          } as typeof prev;
        },
      });
      const moreCount =
        (res.data.visitorChatHistory as { items?: unknown[] } | undefined)?.items?.length ?? 0;
      if (moreCount === 0) setReachedHistoryStart(true);
    } catch {
      // Ignore — the visitor can try again by scrolling up.
    } finally {
      setLoadingOlder(false);
    }
  }, [
    isDummy,
    chatId,
    oldestMessageId,
    loadingOlder,
    reachedHistoryStart,
    historyHasMore,
    fetchMoreHistory,
  ]);

  // SSE realtime is owned by the shell now — it keeps one EventSource
  // open across the whole widget lifetime so the launcher peek + home
  // block keep updating even when this tab isn't mounted. We hand the
  // shell a stable callback that re-reads the latest `handleSseEvent`
  // via a ref, so changes to dependencies (locale, isOpen, etc.) don't
  // re-register and don't reopen the upstream stream.
  const handlerRef = useRef<(event: RealtimeMessageEvent) => void>(() => {});
  useEffect(() => {
    handlerRef.current = (event) => handleSseEvent(event);
  });
  useEffect(() => {
    if (!registerRealtimeHandler) return;
    return registerRealtimeHandler((event) => handlerRef.current(event));
  }, [registerRealtimeHandler]);

  const [send, { loading: sending }] = useSendVisitorMessage();
  const [signalTyping] = useSignalVisitorTypingMutation();

  // Throttle visitor-typing signals — BE relay decays at ~4 s on the
  // operator's chatActivity subscription, so one fire every 3 s keeps
  // the operator's "Visitor is typing…" bubble continuously visible
  // without burning a mutation per keystroke. `lastTypingFireRef`
  // is a render-stable timestamp so the throttle survives re-renders
  // triggered by `setDraft`. Swallows errors — typing is cosmetic.
  const lastTypingFireRef = useRef(0);
  const fireTypingSignal = useCallback(() => {
    if (isDummy || !token) return;
    const now = Date.now();
    if (now - lastTypingFireRef.current < 3000) return;
    lastTypingFireRef.current = now;
    // Scope the signal to the FOCUSED thread so "typing" shows on that thread
    // only. Omitted for a not-yet-created thread (chatId null) → the BE falls
    // back to the visitor's main chat.
    signalTyping({ variables: { chatId: chatId ?? undefined } }).catch(() => undefined);
  }, [signalTyping, isDummy, token, chatId]);

  const handleSseEvent = (event: RealtimeMessageEvent) => {
    if (clearsPendingReply(event)) pendingReply.clear();
    // Unify the two BE payload shapes — new emitters use `from`, old
    // ones used `sender`. Pick whichever is set.
    const sender = event.from ?? event.sender;
    const senderKind = sender?.kind;
    const senderName = sender?.name ?? "";

    if (event.kind === "typing") {
      // Always show AI_ASSISTANT_NAME for ai/bot regardless of whatever the BE
      // labels them ("WEB widget", "Bot", an internal handle, …) so
      // the typing indicator matches the bubble's sender name.
      // Operator names flow through unchanged; the empty-name
      // fallback ("Someone") is handled inside `TypingBubble`.
      const displayName =
        senderKind === "ai" || senderKind === "bot" ? (aiName ?? AI_AGENT_ROLE) : senderName;
      // Same beat as the last one (the server repeats it every ~4 s) → keep the
      // current object so React bails out and nothing re-renders.
      setTypingActor(
        nextTypingActor({
          name: displayName,
          kind: senderKind,
          avatar: sender?.avatar,
          phase: event.phase,
        }),
      );
      // Replace the prior beat's timer (don't stack) and use 8s — ≥ 2× the
      // server's ~4s typing heartbeat — so a late/jittered beat can't let the
      // bubble blink out between heartbeats. A real message clears it sooner.
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setTypingActor(null), 8000);
      return;
    }
    if (event.kind === "read-receipt") {
      // Operator read the visitor's INBOUND messages — or another
      // tab of the same visitor marked operator OUTBOUND messages
      // as read. Either way we layer the ids into `readMessageIds`;
      // the bubble renderer forces deliveryStatus=READ on match.
      const ids = event.readReceipt?.messageIds ?? [];
      if (ids.length > 0) {
        setReadMessageIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.add(id);
          return next;
        });
      }
      return;
    }
    // A real message (text/buttons/media) — or any other non-typing event —
    // ends the typing state NOW: cancel the auto-dismiss timer (so a stale
    // beat timer can't fire later) and drop the bubble immediately rather
    // than letting it linger up to the timeout.
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setTypingActor(null);

    // Inbound (non-visitor) content appends + the inbound chime are
    // both owned by the shell now — the chime call moved up there so
    // the launcher / non-Messages tabs still get an audible cue (this
    // tab isn't mounted in those routes). Avoid double-playing by NOT
    // firing `onInbound` here for the SSE path.
    // Ack is gated on (isOpen && atBottom) — the shell's noteInbound
    // bumped `unreadCount`, the ack effect picks it up on the next
    // render and fires `markChatRead` only when the visitor's at the
    // bottom of the list.
  };

  // Combined message list (real history + live appends / mock).
  // Dedupe by id so an SSE-delivered message that *also* shows up
  // in a later history fetch (page reload while the bubble was
  // already in `liveMessages`, or BE replaying buffered events on
  // SSE reconnect) doesn't render twice. History first → live
  // entries with matching ids drop out so the visitor sees a
  // single bubble per message.
  // Memoised — `mapHistoryToMessages` walks every history row + builds a
  // Set + filters live entries, all O(n). Without memoisation this ran
  // on every render, including every composer keystroke, and turned into
  // visible typing lag on long threads. Deps are the upstream inputs;
  // identity stability of `historyData` is the gating factor (Apollo
  // returns the same reference until the cache writes).
  //
  // NOTE: history rows win over live rows on id collision, and a
  // history-mapped OPERATOR_CONNECTED item never carries `operatorName`
  // (see `message-mapping.ts`). So a `visitorChatHistory` refetch mid-
  // session — while a live "You're now chatting with {name}" row is still
  // showing — downgrades it to the generic "Operator connected" text.
  // Accepted: it's the same degradation a reload already produces, not a
  // new bug introduced by the merge.
  // Mapped on its own so an incoming live message does not re-map (and re-format
  // the timestamp of) the whole history - `toLocaleTimeString` per row dominated
  // the cost of one SSE message on a long thread.
  const mappedHistory = useMemo(
    () => (isDummy ? [] : mapHistoryToMessages(historyData, locale, aiName ?? AI_AGENT_ROLE)),
    [isDummy, historyData, locale, aiName],
  );
  const mergedMessages: MockMessage[] = useMemo(() => {
    if (isDummy) return liveMessages;
    return mergeHistoryAndLive(mappedHistory, liveMessages);
  }, [isDummy, liveMessages, mappedHistory]);
  // The merge above mints a fresh object per row on every upstream change; keep
  // the previous objects for rows whose content is unchanged so the memoised
  // list re-renders only the rows that really changed (PF-01).
  const messages = useReusedMessages(mergedMessages);

  // ─── Unread divider ───────────────────────────────────────────────
  // A one-time "unread messages" divider marking where the visitor's
  // unread run begins, scrolled to the CENTRE of the viewport on open so
  // they land on the first thing they haven't read.
  //
  // Snapshot the unread count at the FIRST render it's known to be > 0 —
  // captured in the render body, BEFORE the auto-ack effect below zeroes
  // it. The shell fetches `visitorChatUnread` on page load, so the count
  // is already populated by the time this tab mounts.
  const snapshotUnreadRef = useRef(0);
  if (snapshotUnreadRef.current === 0 && unreadCount > 0) {
    snapshotUnreadRef.current = unreadCount;
  }
  const [unreadAnchorId, setUnreadAnchorId] = useState<string | null>(null);
  const unreadDividerRef = useRef<HTMLDivElement | null>(null);
  const dividerComputedRef = useRef(false);
  // The thread seed (set during render below): never counted as unread.
  const headerSeedIdRef = useRef<string | null>(null);
  // Ref mirror of the anchor so the bottom-scroll effect's DEFERRED
  // callbacks (rAF / ResizeObserver, which capture a stale closure) read the
  // LATEST value — otherwise a divider that resolves a tick after the effect
  // ran would let those callbacks bottom-scroll right past it.
  const unreadAnchorRef = useRef<string | null>(null);
  unreadAnchorRef.current = unreadAnchorId;

  // Compute the anchor ONCE, after the first history page settles. The
  // first unread message is the `snapshotUnread`-th OUTBOUND (operator/ai)
  // message from the end — the visitor's own messages aren't "unread".
  useEffect(() => {
    if (dividerComputedRef.current) return;
    if (historyLoading) return;
    dividerComputedRef.current = true;
    const count = snapshotUnreadRef.current;
    // Demo: the seeded unread count only drives the list badge. Its thread is media-heavy and the
    // images land late, so centring a divider would park the visitor mid-thread (the newest
    // message off-screen) - the demo opens at the bottom like every other open.
    if (count > 0 && messages.length > 0 && !isDummy) {
      // Anchor = the FIRST unread message = the `count`-th OUTBOUND (operator/AI)
      // message from the end. Only accept it when we actually reach the `count`-th
      // within the loaded page: if the unread run extends BEYOND the first
      // history page (fewer than `count` outbound loaded), the true first-unread
      // isn't loaded yet — placing the divider on the oldest loaded message would
      // fling the open-scroll to the top. In that case leave no anchor → normal
      // bottom scroll, no divider.
      const anchor = findUnreadAnchor(messages, count, headerSeedIdRef.current);
      if (anchor) setUnreadAnchorId(anchor);
    }
  }, [historyLoading, messages, isDummy]);

  // Center the divider in the viewport ONCE it mounts — its own effect, so
  // the NO-unread path (no anchor) never touches the normal bottom-scroll.
  // `useLayoutEffect` so the centring happens BEFORE the browser paints — no
  // flash of the thread top before it jumps to the divider.
  useLayoutEffect(() => {
    if (!unreadAnchorId) return;
    const divider = unreadDividerRef.current;
    if (!divider) return;
    didInitialScrollRef.current = true; // the divider IS the initial position
    divider.scrollIntoView({ block: "center", behavior: "auto" });
  }, [unreadAnchorId]);

  // Auto-dismiss the divider ~4 s after it appears: it's a transient "here's
  // where you left off" marker, not a permanent rule — the visitor glances at
  // it, then it fades (AnimatePresence exit on `UnreadDivider`) so the thread
  // reads as normal. The timer arms whenever an anchor is set (first mount OR a
  // re-open recompute), and is cleared on unmount / anchor change so a re-open
  // gets a fresh 4 s. Clearing only the anchor leaves the visitor's scroll
  // position untouched (the centre-scroll already happened) — the bottom-scroll
  // effect won't yank them since they're parked mid-thread on the divider, not
  // at the bottom.
  useEffect(() => {
    if (!unreadAnchorId) return;
    const timer = setTimeout(() => {
      unreadAnchorRef.current = null;
      setUnreadAnchorId(null);
    }, DIVIDER_LINGER_MS);
    return () => clearTimeout(timer);
  }, [unreadAnchorId]);

  // The messenger tab stays MOUNTED across widget open/close (the shell only
  // hides the panel — see `hasOpened`). So the one-shot divider + initial
  // scroll above run only on FIRST mount; on a RE-open, messages that arrived
  // while the widget was closed would otherwise just sit at the bottom with
  // no divider. On each re-open (isOpen false→true) recompute the anchor from
  // the current unread and re-arm the open scroll — the same as a fresh open.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const reopened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!reopened) return;
    let anchor: string | null = null;
    if (unreadCount > 0 && messages.length > 0 && !isDummy) {
      // Same clamp as the first-mount anchor: only place the divider when the
      // full unread run is within the loaded page, else no divider (avoids the
      // open-scroll flinging to the top on reopen).
      let seen = 0;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].direction === "OUTBOUND") {
          seen += 1;
          if (seen === unreadCount) {
            anchor = messages[i].id;
            break;
          }
        }
      }
    }
    // Sync the ref synchronously so the (same-flush) bottom-scroll effect sees
    // the new anchor and defers to the centering effect — no flash to bottom.
    unreadAnchorRef.current = anchor;
    didInitialScrollRef.current = false; // re-arm the open scroll (center / bottom)
    setUnreadAnchorId(anchor);
  }, [isOpen, unreadCount, messages, isDummy]);

  // Track scroll position so we can:
  //   1. skip the auto-scroll-on-new-message dance when the visitor
  //      is reading history (yanking them to the bottom would be
  //      hostile), and
  //   2. delay the read-ack until the new bubble is actually in
  //      view (visitor at bottom).
  // ~80 px slack — accounts for a partial bubble at the bottom edge
  // still counting as "in view."
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      const near = dist < 80;
      atBottomRef.current = near;
      // Show the "scroll to newest" button once the visitor is meaningfully
      // scrolled up (React bails out when the boolean is unchanged, so this is
      // cheap on every scroll tick).
      setShowScrollDown(dist > 160);
      // When the visitor genuinely reaches the bottom (manual scroll, or
      // post-send), re-enable the bottom-pin — they've caught up to any new
      // content that arrived "behind their back".
      if (near) bottomPinAllowedRef.current = true;
      // Top-edge detector → trigger a page of older messages. 120-px
      // pre-roll so the fetch starts before the visitor hits the
      // hard top — the prepend completes by the time they actually
      // scroll into the old region. `loadOlder` self-debounces via
      // `loadingOlder` so a fast scroll doesn't spam requests.
      if (el.scrollTop < 120) loadOlder();
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    // Re-pin to the bottom whenever a media element finishes loading IF the
    // visitor was already at the bottom. The initial-scroll effect's
    // ResizeObserver only watches for ~2 s — a slow image / large video that
    // arrives after that window grew the content and pushed the visitor up.
    // This catches every late media load for the lifetime of the tab.
    // `capture: true` is required: `load` doesn't bubble.
    const onMediaLoad = (e: Event) => {
      const target = e.target;
      if (!(target instanceof HTMLImageElement || target instanceof HTMLVideoElement)) {
        return;
      }
      // Only re-pin if the bottom-pin is ALLOWED. After a new operator/AI
      // message lands while visitor was at bottom, `bottomPinAllowedRef` is
      // false — `atBottomRef` may still be stale-true (no scroll event fired
      // since content grew), but pinning here would silently scroll past the
      // new message. This guard preserves the "stay where I was" intent.
      const settling = Date.now() < settleToBottomUntilRef.current;
      if (!settling && (!atBottomRef.current || !bottomPinAllowedRef.current)) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    };
    // The visitor taking over the scroll ends the settle window.
    const onUserScroll = () => {
      settleToBottomUntilRef.current = 0;
    };
    el.addEventListener("load", onMediaLoad, true);
    el.addEventListener("wheel", onUserScroll, { passive: true });
    el.addEventListener("touchmove", onUserScroll, { passive: true });
    el.addEventListener("keydown", onUserScroll);
    return () => {
      el.removeEventListener("wheel", onUserScroll);
      el.removeEventListener("touchmove", onUserScroll);
      el.removeEventListener("keydown", onUserScroll);
      el.removeEventListener("scroll", update);
      el.removeEventListener("load", onMediaLoad, true);
    };
  }, [loadOlder]);

  // Preserve scroll position when older messages are prepended.
  // Without this the visitor's viewport visibly jumps upward when
  // a new page lands (`scrollTop` stays the same but the content
  // got taller, so the bubble they were reading is now somewhere
  // off-screen). We snapshot the scroll geometry before each
  // fetchMore and restore the offset on the next render.
  const preFetchScrollRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (loadingOlder) {
      preFetchScrollRef.current = {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
      };
      return;
    }
    const snapshot = preFetchScrollRef.current;
    if (!snapshot) return;
    preFetchScrollRef.current = null;
    // The fetch is settled; restore the offset so the previously-
    // visible message stays anchored under the visitor's gaze.
    const delta = el.scrollHeight - snapshot.scrollHeight;
    if (delta > 0) {
      el.scrollTop = snapshot.scrollTop + delta;
    }
  }, [loadingOlder]);

  // `false` until the very first auto-scroll has happened, then
  // `true` for the rest of the tab's lifetime. The initial mount
  // ignores `atBottomRef` and *forces* a scroll to the last
  // message — otherwise the bottom-detector that fires on the very
  // first scroll event (triggered by content layout, before any
  // real visitor scroll) flips `atBottom` to false and our
  // "only auto-scroll if visitor is at the bottom" guard skips
  // the initial scroll, leaving the visitor mid-conversation.
  const didInitialScrollRef = useRef(false);
  // Tracks the previous render's message count so the scroll effect can tell
  // a NEW arrival from a no-op re-run. When a new operator/AI message lands
  // we scroll to its TOP (not the thread's bottom) so a long message starts
  // at the top of the viewport instead of pinning its tail.
  const prevMessagesLengthRef = useRef(0);

  // The viewport itself changing size (rotate, keyboard, window resize) re-pins a reader who was
  // at the bottom; the content observer below only watches the content for ~2 s.
  useRepinOnResize(
    scrollRef,
    () =>
      bottomPinAllowedRef.current &&
      !unreadAnchorRef.current &&
      !(snapshotUnreadRef.current > 0 && !dividerComputedRef.current),
    // New messages and the typing row are placed by the effect below; the hook follows only the
    // content settling under the reader (late media, the demo text bundle).
    `${messages.length}:${shownTyping ? 1 : 0}`,
  );

  // Auto-scroll bottom on new messages.
  //
  //   • First mount (or first messages payload arriving) → ALWAYS
  //     scroll to the bottom, regardless of `atBottomRef`. Visitors
  //     expect the conversation to open on the latest exchange.
  //   • Subsequent updates → only auto-scroll if the visitor was
  //     already near the bottom, so we don't yank them out of the
  //     history they were reading when a new message lands.
  //
  // Three-stage scroll strategy applies in both cases:
  //   1. Immediate scroll (current frame) — covers text-only
  //      messages and the initial commit on tab open.
  //   2. `requestAnimationFrame` retry — picks up the next layout
  //      pass after React commits the new bubbles.
  //   3. **ResizeObserver on the content** — async media (images,
  //      audio waveforms loading via fetch, video metadata) grow
  //      the content AFTER the messages array stops changing.
  //      Without this, a peek-tap landing on a fresh conversation
  //      parked mid-list because the auto-scroll fired before the
  //      images finished loading.
  //
  // `typingActor` is a deliberate re-scroll trigger: when the typing
  // bubble appears/disappears the content height changes, so we re-run
  // the scroll strategy even though the body reads it via refs.
  //
  // `useLayoutEffect` (not `useEffect`) so the INITIAL positioning lands
  // synchronously BEFORE the browser paints: on a thread open the history
  // populates, this scrolls to the bottom, and the FIRST painted frame is
  // already at the bottom. With a plain effect the browser painted one frame at
  // the top first (showing the thread's oldest message / a tall image) and then
  // jumped down — the "flash" the visitor saw when opening a thread with media.
  // biome-ignore lint/correctness/useExhaustiveDependencies: shownTyping is an intentional re-scroll trigger, not an unused dep
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isInitial = !didInitialScrollRef.current && messages.length > 0;
    // Did the messages array grow this render? If the latest is from the
    // operator/AI (not the visitor's own echo) we'll target the TOP of its
    // bubble below, so long messages start at the top of view.
    const prevLen = prevMessagesLengthRef.current;
    const grew = messages.length > prevLen;
    prevMessagesLengthRef.current = messages.length;
    const lastMsg = messages[messages.length - 1];
    const isNewFromOther = grew && !isInitial && !!lastMsg && lastMsg.sender.kind !== "visitor";
    // First newly-appended message in this render's growth window — the
    // peek-scroll below uses it as the anchor so multi-message bursts
    // (a flow firing several inbounds in a row) park the visitor at the
    // TOP of the burst, not the tail.
    const firstNewMsg = grew ? messages[prevLen] : null;
    // Did the visitor send this growth? Their own send always re-enables bottom-pin and scrolls
    // (they expect to see their bubble). An EXPLICIT signal from the send paths: growth plus a
    // visitor-authored newest row is also what prepended history or a refetch looks like.
    const ownSendSignalled = grew && ownSend.consume();
    const isVisitorSend =
      ownSendSignalled && !isInitial && !!lastMsg && lastMsg.sender.kind === "visitor";
    if (holdsScroll({ isInitial, atBottom: atBottomRef.current, isVisitorSend })) return;
    const content = el.firstElementChild;
    // Initial scroll is `auto` (instant) so the visitor never sees
    // the smooth-scroll animate from the top; subsequent ones use
    // `smooth` so new-message landings feel natural.
    // `forceInstant` — the ResizeObserver passes this while content is still
    // GROWING (media reserving/loading after the messages array settled). That
    // is a "keep the bottom pinned" op, not a "navigate to a new message" one,
    // so it must be INSTANT: a smooth animation re-triggered on every media
    // load is exactly the harsh, repeated glide-down the visitor sees when a
    // long thread opens. New-message landings (direct + rAF) stay smooth.
    const scroll = (forceInstant = false) => {
      if (holdsScroll({ isInitial, atBottom: atBottomRef.current, isVisitorSend })) return;
      // Hold off while an unread divider is pending/active — the visitor
      // should land on (and read down from) the divider, NOT the bottom.
      // Reads refs so the deferred rAF/ResizeObserver see the latest state.
      // The visitor's OWN send is exempt: they have clearly read on, and the bubble they just
      // wrote must be visible.
      if (
        !isVisitorSend &&
        (unreadAnchorRef.current || (snapshotUnreadRef.current > 0 && !dividerComputedRef.current))
      ) {
        return;
      }
      // New operator/AI message — two behaviours depending on bubble
      // size, both gated on "visitor was at the bottom":
      //
      //   • SHORT bubble (fits comfortably in the viewport) → scroll
      //     all the way to the bottom so the whole thing is visible.
      //     The visitor was already engaged; a small message
      //     materialising in full reads as natural conversation pace.
      //
      //   • LONG bubble (taller than the threshold) → peek scroll.
      //     Surface ~80 px of the bubble's TOP edge. The visitor sees
      //     the message arriving, knows there's more, and scrolls down
      //     at their own pace to read it.
      //
      // The threshold is 60% of the viewport — under that, a full-show
      // doesn't drown out prior context above; over that, peeking
      // signals "more below" without committing the visitor's eye to
      // re-reading the whole new message from its tail.
      //
      // If the visitor was NOT at the bottom (mid-scroll reading
      // history), leave them exactly where they are.
      //
      // Either branch drops the bottom-pin so ambient triggers (typing
      // beats, avatar/image loads) don't accidentally yank them past
      // the new bubble afterwards.
      if (isNewFromOther) {
        bottomPinAllowedRef.current = false;
        // Stale-but-meaningful: `atBottomRef` reflects the LAST scroll
        // event's position, captured BEFORE this growth landed. If it
        // was true, the visitor was parked at the bottom of the prior
        // content — exactly the case where the peek/full scroll helps.
        if (!atBottomRef.current) return;
        const newBubble = firstNewMsg
          ? (el.querySelector(`[data-message-id="${firstNewMsg.id}"]`) as HTMLElement | null)
          : null;
        if (!newBubble) return;
        const bubbleHeight = newBubble.getBoundingClientRect().height;
        const viewportH = el.clientHeight;
        const FULL_SHOW_RATIO = 0.6;
        if (bubbleHeight <= viewportH * FULL_SHOW_RATIO) {
          // Short — go to the literal bottom of content.
          el.scrollTo({ top: el.scrollHeight - viewportH, behavior: "smooth" });
          // The whole bubble is in view at the literal bottom, so nothing is being scrolled
          // PAST: keep the pin so media still loading above (a peek tap opens the demo thread
          // while its images load) re-pins instead of leaving the visitor mid-thread.
          bottomPinAllowedRef.current = true;
          settleToBottomUntilRef.current = Date.now() + 3000;
        } else {
          // Long — peek the top 80 px so the visitor sees the message
          // start without being teleported past it.
          const containerRect = el.getBoundingClientRect();
          const bubbleRect = newBubble.getBoundingClientRect();
          const bubbleTopInContent = bubbleRect.top - containerRect.top + el.scrollTop;
          const PEEK_PX = 80;
          const target = bubbleTopInContent - (viewportH - PEEK_PX);
          const clamped = Math.max(0, Math.min(el.scrollHeight - viewportH, target));
          el.scrollTo({ top: clamped, behavior: "smooth" });
        }
        return;
      }
      // Visitor's own send → force-enable the pin + scroll to bottom (their
      // explicit action; bubble must be visible).
      if (isVisitorSend) {
        bottomPinAllowedRef.current = true;
      }
      // Any other ambient bottom-scroll (typing beats, periodic re-runs)
      // honours the lock — once a non-visitor message arrived "behind" the
      // visitor, nothing else yanks them to the bottom.
      if (!isInitial && !bottomPinAllowedRef.current) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: isInitial || forceInstant ? "auto" : "smooth",
      });
    };
    scroll();
    if (isInitial) {
      didInitialScrollRef.current = true;
      // After the first scroll, sync `atBottomRef` so the next
      // tick's scroll handler doesn't re-flag us as "not at bottom"
      // (we just put ourselves there programmatically).
      atBottomRef.current = true;
    }
    const raf = requestAnimationFrame(() => scroll());
    let ro: ResizeObserver | null = null;
    if (content && typeof ResizeObserver !== "undefined") {
      // Stop observing after ~2 s — by then any sensible media has
      // loaded, and we don't want the observer pulling the visitor
      // back to the bottom if they scrolled up to read history.
      // `scroll(true)` → INSTANT: content growth from async media keeps the
      // bottom pinned without an animated glide (the "harsh scroll" on open).
      ro = new ResizeObserver(() => scroll(true));
      ro.observe(content);
      const stopTimer = window.setTimeout(() => ro?.disconnect(), 2000);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(stopTimer);
        ro?.disconnect();
      };
    }
    return () => cancelAnimationFrame(raf);
  }, [messages.length, shownTyping, unreadAnchorId, isOpen]);

  // Fire the BE read-ack when the visitor has actually SEEN the new
  // messages — not just when they were near-bottom before the message
  // arrived. We attach a fresh scroll listener so we see EVERY scroll
  // position, not just the `atBottom` state flip (which trips at the
  // 120 px "near bottom" threshold and would miss the final glide down
  // to the literal bottom — read-acks need certainty the visitor saw
  // the content, not just that they were close).
  //
  // Also re-runs on `messages.length` so a freshly-arrived inbound
  // gets an immediate strict-bottom check: visitor was at bottom of
  // OLD content, growth pushes them ~bubble-height above the new
  // bottom, the check fails, message stays unread until they scroll
  // down. The earlier "ack on stale `atBottom: true`" bug acked
  // messages before they were seen.
  //
  // Strict threshold (20 px) — tighter than the auto-scroll's 120 px
  // "near bottom" tolerance.
  //
  // Guarded by `ackingRef` inside `ackRead` so it only fires once per
  // unread → read transition.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `messages.length` is a deliberate re-trigger — every content commit needs a fresh strict-bottom check (a freshly-arrived inbound puts the visitor `bubbleHeight` below the new bottom, so we re-verify after each render).
  useEffect(() => {
    if (!isOpen) return;
    if (unreadCount === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const STRICT_BOTTOM_PX = 20;
    const tryAck = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist <= STRICT_BOTTOM_PX) ackRead();
    };
    // Immediate check covers the "already at bottom on mount" path
    // (peek tap, reload with pending unreads).
    tryAck();
    el.addEventListener("scroll", tryAck, { passive: true });
    return () => el.removeEventListener("scroll", tryAck);
  }, [isOpen, unreadCount, ackRead, messages.length]);

  // ID of the latest outbound message whose quick-reply buttons are
  // still actionable. Buttons render UNDER the bubble itself (in the
  // message container) so the visitor sees them attached to the
  // sender's reply, not floating in the composer. Only the latest
  // outbound run shows interactive chips; older runs are hidden via
  // `consumedButtonIds`.
  const activeButtonMessageId: string | null = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.direction !== "OUTBOUND") break; // visitor responded already
      if (m.buttons && m.buttons.length > 0 && !consumedButtonIds.has(m.id)) {
        return m.id;
      }
    }
    return null;
  })();

  // The reply timer fires ~900 ms after the send; it must read the demo content as of THEN, not the
  // (possibly still empty) translator of the render that scheduled it.
  const tDemoRef = useRef(tDemo);
  tDemoRef.current = tDemo;

  const sendDummyExchange = (text: string, media?: MockMessageMedia[]) => {
    const msgId = `tmp-${Date.now()}`;
    appendLive({
      id: msgId,
      direction: "INBOUND",
      text,
      ...(media && media.length > 0 ? { media } : {}),
      ageLabel: "now",
      sender: { kind: "visitor", name: "You" },
      // Optimistic ✓ — flips to ✓✓ when the demo "operator"
      // acks the message via the canned reply path below.
      deliveryStatus: "DELIVERED",
    });
    onOutbound?.();
    // Canned AI reply ~900ms later — and a synthetic "operator
    // read" event ~600ms after that, so the visitor sees ✓ → ✓✓
    // on their own bubble without a real BE round-trip.
    setTimeout(() => {
      const reply = pickDummyReply(text, tDemoRef.current);
      if (!reply.text) return; // never append an empty bubble
      appendLive({
        id: `tmp-fin-${Date.now()}`,
        direction: "OUTBOUND",
        text: reply.text,
        ageLabel: "now",
        sender: { kind: "ai", name: AI_ASSISTANT_NAME },
        buttons: reply.buttons,
      });
      setReadMessageIds((prev) => {
        const next = new Set(prev);
        next.add(msgId);
        return next;
      });
      onInbound?.();
    }, 900);
  };

  // Demo: a launcher-peek quick-reply the visitor tapped before opening
  // the panel. Replay it as a real send (visitor bubble + canned reply)
  // exactly once, then tell the shell to drop it. Ref-guarded so it
  // fires a single time per mount even though `sendDummyExchange` is a
  // fresh closure each render.
  const peekReplySentRef = useRef(false);
  // Waits for the demo bundle: a reply picked from the still-empty translator is blank.
  const demoReplyReady = isDemoReplyReady(tDemo);
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot replay — guarded by the ref, `sendDummyExchange`/`onInitialVisitorMessageSent` deliberately excluded
  useEffect(() => {
    if (!isDummy || !initialVisitorMessage || !demoReplyReady || peekReplySentRef.current) return;
    peekReplySentRef.current = true;
    sendDummyExchange(initialVisitorMessage);
    onInitialVisitorMessageSent?.();
  }, [isDummy, initialVisitorMessage, demoReplyReady]);

  const handleQuickReply = (messageId: string, button: MockMessageButton) => {
    // Non-callback buttons act LOCALLY and post nothing to the chat — no
    // optimistic bubble, no mutation, and no "consume" (a link/tel tap doesn't
    // answer the flow card, so the message's callback buttons stay active).
    if (button.type === "url") {
      if (isSafeExternalUrl(button.url)) {
        trackLinkClick({
          url: button.url,
          targetType: "CTA_URL",
          targetRefId: messageId,
          title: button.text,
        });
        openExternalUrl(button.url);
      }
      return;
    }
    if (button.type === "phone") {
      openPhone(button.phoneNumber);
      return;
    }
    if (button.type && button.type !== "callback") {
      // A `tg_*` / `wa_*` type that shouldn't reach web — ignore it rather than
      // post a bogus callback to the chat.
      return;
    }

    // callback (default): resume the flow by value.
    setConsumedButtonIds((prev) => {
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
    if (isDummy) {
      sendDummyExchange(button.text);
      return;
    }
    // Offline outbox path — a quick-reply is a text send carrying the button's
    // callback `value`. Route it through the shell's queue so it persists +
    // retries like any other send.
    if (enqueueSend) {
      onOutbound?.();
      enqueueSend({
        text: button.text,
        threadKey: threadKey ?? null,
        chatId: chatId ?? null,
        // A welcome quick reply has no flow `value`: sending "" would route the BE down
        // the callback path, so it is omitted (a plain visitor message).
        buttonValue: button.payload || undefined,
      });
      return;
    }
    // Optimistic visitor bubble. The previous version only fired the
    // mutation, so the chip-click didn't paint a "You: …" row until
    // the BE round-trip completed (and only then via the next history
    // refetch or SSE echo) — leaving the visitor staring at a chat
    // that visibly "ate" their tap. Append a tmp row now and swap
    // the id with the BE-canonical one on resolve, mirroring the
    // typed-send path.
    const optimisticId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    appendLive({
      id: optimisticId,
      // Stable render key — survives the tmp-id → BE-id swap below
      // so React doesn't unmount + remount the bubble (which would
      // re-play the entrance animation when an SSE event triggers
      // the next list render).
      clientKey: optimisticId,
      direction: "INBOUND",
      text: button.text,
      ageLabel: "now",
      sender: { kind: "visitor", name: "" },
      deliveryStatus: "SENT",
    });
    onOutbound?.();
    // Send the button's `value` (callback data) alongside the label so the BE
    // resumes the flow deterministically by value — not by matching the raw
    // label against whatever card is currently awaiting input. The widget
    // button carries the BE `value` as `payload` (see message-mapping.ts).
    // Only quick-reply chip clicks send `buttonValue` — typed/media/location
    // sends must not.
    // SEND boundary — a flow's button-reply MUST carry the active thread key.
    send({
      variables: {
        input: {
          text: button.text,
          buttonValue: button.payload || undefined,
          // Carry the active thread's key so a flow's button-reply stays on THIS
          // thread (not the main chat) — otherwise the armed flow never resumes.
          ...slotRouting(slotSend()),
        },
      },
    })
      .then((res) => {
        const ack = res.data?.sendVisitorMessage;
        const realId = ack?.messageId;
        if (realId) {
          replaceLiveMessage?.(optimisticId, {
            id: realId,
            direction: "INBOUND",
            text: button.text,
            ageLabel: "now",
            sender: { kind: "visitor", name: "" },
            deliveryStatus: "DELIVERED",
          });
        }
      })
      .catch(() => {});
  };

  const handleFileChosen = async (file: File) => {
    setUploadError(null);
    // Resolve the MIME up front — mobile camera photos often arrive with
    // an empty `File.type`, so fall back to the filename extension. Use
    // this resolved value for BOTH the allow-list check and every payload
    // below, so the BE receives a real MIME instead of an empty string.
    const mimetype = resolveUploadMime(file);
    if (!ALLOWED_UPLOAD_MIMES.has(mimetype)) {
      setUploadError(t("fileMimeNotAllowed"));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(t("fileTooLarge"));
      return;
    }
    if (isDummy) {
      // Demo: skip the upload mutation; just append a local chip
      // with a fake media id so the visitor sees their file in the
      // attachment row.
      setAttachments((prev) => [
        ...prev,
        {
          tempId: `upl-${Date.now()}`,
          filename: file.name,
          mimetype,
          mediaId: "demo-media",
          // Local preview URL so the demo bubble actually shows the file.
          url: URL.createObjectURL(file),
        },
      ]);
      return;
    }
    const tempId = `upl-${Date.now()}`;
    // Local preview URL so the image/video tile renders the actual attached
    // file IMMEDIATELY (with the spinner overlay), instead of showing a
    // generic file icon until the S3 upload completes. Captured in a const
    // so the resolved handler can revoke it once the S3 url replaces it.
    const localUrl = URL.createObjectURL(file);
    // Compute base64 up front so the chip can carry the raw bytes: if the
    // upload fails (offline / server down) we KEEP the chip as "pending" and
    // let the offline queue upload it at send/drain time, instead of erroring.
    const { base64 } = await fileToBase64(file);
    // Offline → don't even attempt the upload (it would spin forever). Add the
    // chip as PENDING with a local preview + its bytes; the send queues it and
    // the outbox uploads it at drain. `uploading: false` so the chip shows the
    // preview ready-to-send instead of a stuck spinner.
    if (outbox && !outbox.online) {
      setAttachments((prev) => [
        ...prev,
        {
          tempId,
          filename: file.name,
          mimetype,
          mediaId: null,
          url: localUrl,
          dataBase64: base64,
          uploading: false,
        },
      ]);
      return;
    }
    setAttachments((prev) => [
      ...prev,
      {
        tempId,
        filename: file.name,
        mimetype,
        mediaId: null,
        url: localUrl,
        dataBase64: base64,
        uploading: true,
      },
    ]);
    try {
      const res = await uploadFile({
        variables: {
          input: {
            filename: file.name,
            mimetype,
            dataBase64: base64,
          },
        },
      });
      const out = res.data?.uploadVisitorMedia;
      if (!out) throw new Error("empty upload response");
      // If the visitor removed the chip while the upload was still in flight,
      // the freshly-minted media is now an orphan on the BE — clean it up.
      // The chip is already gone from state, so the map below is a no-op.
      if (removedDuringUploadRef.current.delete(tempId)) {
        removeUploadedMedia({ variables: { mediaId: out.mediaId } }).catch(() => undefined);
      }
      // Replace the local preview with the canonical S3 url + free the now-
      // orphaned blob so it doesn't pin in memory for the page lifetime. Drop
      // the base64 (uploaded — no longer needed for the queue).
      if (out.url) URL.revokeObjectURL(localUrl);
      setAttachments((prev) =>
        prev.map((a) =>
          a.tempId === tempId
            ? {
                ...a,
                mediaId: out.mediaId,
                dataBase64: undefined,
                uploading: false,
                ...(out.url ? { url: out.url } : {}),
              }
            : a,
        ),
      );
    } catch {
      // Upload failed (e.g. BE down while online) — stop the spinner and keep
      // the chip PENDING (it holds `dataBase64` + the local preview). The
      // offline queue uploads it when the send drains. No error surfaced: the
      // attachment is valid, just not uploaded yet.
      setAttachments((prev) =>
        prev.map((a) => (a.tempId === tempId ? { ...a, uploading: false } : a)),
      );
    }
  };

  const removeAttachment = (tempId: string) => {
    const chip = attachments.find((a) => a.tempId === tempId);
    // Free the local preview blob (created in `handleFileChosen`) so the
    // browser doesn't pin it for the page lifetime.
    if (chip?.url?.startsWith("blob:")) URL.revokeObjectURL(chip.url);
    if (chip && !isDummy) {
      if (chip.mediaId) {
        // Upload finished — drop the orphan on the BE. Idempotent + rate-
        // limit-guarded, so fire-and-forget on failure.
        removeUploadedMedia({ variables: { mediaId: chip.mediaId } }).catch(() => undefined);
      } else {
        // Upload still in flight — mark so the resolve handler in
        // `handleFileChosen` removes the orphan once the BE mints it.
        removedDuringUploadRef.current.add(tempId);
      }
    }
    setAttachments((prev) => prev.filter((a) => a.tempId !== tempId));
  };

  /**
   * Voice recording is owned by `VoiceRecorderBar` (which wraps a
   * `wavesurfer.js` `RecordPlugin` instance). The mic button simply
   * mounts that bar in place of the composer; the bar fires
   * `onComplete` with the encoded blob when the visitor taps send,
   * or `onCancel` when they bail. Either way, we close the recorder
   * surface and the textarea returns.
   */
  const startRecording = async () => {
    setUploadError(null);
    // A voice note must not bypass a required prechat — collect + submit
    // the details first; bail (showing errors) if they're missing.
    if (!(await ensurePrechat())) return;
    setRecording(true);
  };

  /**
   * Voice send is single-tap: the recorder bar's Send button hands
   * us an encoded blob, we upload it + ship a visitor message
   * carrying the resulting `mediaId` in one go. Skips the attachment
   * row entirely — visitors expect a voice note to behave like a
   * message, not a draft. Demo mode optimistically appends an
   * inbound bubble that renders the local blob URL so the visitor
   * sees their take in the conversation immediately.
   */
  const handleRecordingComplete = async (rec: VoiceRecording) => {
    setRecording(false);
    if (rec.size > MAX_UPLOAD_BYTES) {
      setUploadError(t("fileTooLarge"));
      return;
    }
    if (isDummy) {
      // Convert the base64 payload back into a Blob URL so the
      // optimistic bubble actually plays back the recording.
      const blob = base64ToBlob(rec.base64, rec.mimetype);
      const url = URL.createObjectURL(blob);
      const msgId = `tmp-voice-${Date.now()}`;
      appendLive({
        id: msgId,
        direction: "INBOUND",
        text: "",
        ageLabel: "now",
        sender: { kind: "visitor", name: "You" },
        media: [{ url, mimetype: rec.mimetype, alt: rec.filename }],
        deliveryStatus: "DELIVERED",
      });
      onOutbound?.();
      // Canned AI reply ~900ms later so the demo conversation feels alive.
      setTimeout(() => {
        const replyText = pickVoiceNoteReply(tDemoRef.current);
        if (!replyText) return; // never append an empty bubble
        appendLive({
          id: `tmp-fin-${Date.now()}`,
          direction: "OUTBOUND",
          text: replyText,
          ageLabel: "now",
          sender: { kind: "ai", name: AI_ASSISTANT_NAME },
        });
        setReadMessageIds((prev) => {
          const next = new Set(prev);
          next.add(msgId);
          return next;
        });
        onInbound?.();
      }, 900);
      return;
    }
    // Offline outbox path — queue the voice note. Try to upload the bytes now;
    // if that fails (offline / server down), queue the RAW bytes and let the
    // drain upload them later. Either way the send is persisted + retried.
    if (enqueueSend) {
      let media: {
        filename: string;
        mimetype: string;
        mediaId?: string;
        s3Url?: string;
        dataBase64?: string;
      }[];
      try {
        const res = await uploadFile({
          variables: {
            input: { filename: rec.filename, mimetype: rec.mimetype, dataBase64: rec.base64 },
          },
        });
        const out = res.data?.uploadVisitorMedia;
        if (!out?.mediaId) throw new Error("empty upload response");
        media = [
          {
            filename: rec.filename,
            mimetype: rec.mimetype,
            mediaId: out.mediaId,
            ...(out.url ? { s3Url: out.url } : {}),
          },
        ];
      } catch {
        // Upload failed → carry the bytes for a drain-time upload.
        media = [{ filename: rec.filename, mimetype: rec.mimetype, dataBase64: rec.base64 }];
      }
      onOutbound?.();
      enqueueSend({ text: "", threadKey: threadKey ?? null, chatId: chatId ?? null, media });
      return;
    }
    // Direct fallback (outbox disabled): upload + optimistic bubble + send.
    try {
      const res = await uploadFile({
        variables: {
          input: {
            filename: rec.filename,
            mimetype: rec.mimetype,
            dataBase64: rec.base64,
          },
        },
      });
      const out = res.data?.uploadVisitorMedia;
      const mediaId = out?.mediaId;
      if (!mediaId) throw new Error("empty upload response");
      const previewUrl = out?.url ?? URL.createObjectURL(base64ToBlob(rec.base64, rec.mimetype));
      const optimisticId = `tmp-voice-${Date.now()}`;
      appendLive({
        id: optimisticId,
        clientKey: optimisticId,
        direction: "INBOUND",
        text: "",
        ageLabel: "now",
        sender: { kind: "visitor", name: "" },
        media: [{ url: previewUrl, mimetype: rec.mimetype, alt: rec.filename }],
        deliveryStatus: "SENT",
      });
      await send({
        variables: { input: { mediaIds: [mediaId], ...(threadKey ? { threadKey } : {}) } },
      });
      onOutbound?.();
    } catch {
      setUploadError(t("uploadFailed"));
    }
  };

  const handleRecordingError = (kind: "denied" | "unsupported" | "failed") => {
    setRecording(false);
    setUploadError(
      kind === "unsupported"
        ? t("voiceUnsupported")
        : kind === "denied"
          ? t("voiceDenied")
          : t("uploadFailed"),
    );
  };

  const performSend = async () => {
    const draft = draftStore.get().text;
    const draftHtml = draftStore.get().html;
    const plain = draft.trim();
    const mediaIds = attachments.map((a) => a.mediaId).filter((id): id is string => Boolean(id));
    // A pending (offline-attached) chip counts as media too — it carries raw
    // bytes the queue uploads later — so the send isn't blocked when only such
    // attachments are present.
    const hasMedia = attachments.some((a) => !!a.mediaId || !!a.dataBase64);
    if ((!plain && !hasMedia) || sending || submittingPrechat) return;
    // Over the char cap → block the send (the counter already warns). The cap
    // counts VISIBLE text (`draft`), not the HTML markup — the message is sent
    // as HTML but the length limit is on the characters the visitor typed. The
    // BE mirrors this (it strips tags before its own length check).
    if (draft.length > MAX_COMPOSER_CHARS) return;

    // The wire payload: sanitised rich-text HTML when the visitor used any
    // formatting, otherwise the plain text (so an unformatted message stays
    // plain and renders through the plain-text branch, unchanged from before).
    const sanitized = sanitizeComposerHtml(draftHtml);
    const text = hasRichFormatting(sanitized) ? sanitized : plain;

    // Renderable media for the visitor's OWN optimistic bubble — without it
    // the just-sent image/file wouldn't show until a later history refetch.
    const optimisticMedia = attachments
      .filter((a): a is AttachmentChip & { url: string } => !!a.mediaId && !!a.url)
      .map((a) => ({ url: a.url, mimetype: a.mimetype, alt: a.filename }));

    // Prechat gate — when the operator requires visitor details, the
    // compact fields above the editor are validated + submitted WITH
    // this first message (profile attached before the message). Abort
    // the send if they're missing/invalid.
    if (!(await ensurePrechat())) return;

    if (isDummy) {
      clearComposer();
      setAttachments([]);
      sendDummyExchange(text, optimisticMedia);
      return;
    }

    // Offline outbox path — text AND media sends go through the shell's
    // persistent queue: it owns the optimistic bubble, the send, retry-on-
    // reconnect, and cross-reload persistence. Each attachment rides as either
    // already-uploaded (`mediaId` + `s3Url`) or PENDING (`dataBase64` — attached
    // offline; the queue uploads it at drain time). So a media message survives
    // both a flaky send AND being composed with no connection.
    if (enqueueSend) {
      const media = attachments
        .filter((a) => !!a.mediaId || !!a.dataBase64)
        .map((a) => ({
          filename: a.filename,
          mimetype: a.mimetype,
          ...(a.mediaId ? { mediaId: a.mediaId } : {}),
          ...(a.mediaId && a.url ? { s3Url: a.url } : {}),
          ...(!a.mediaId && a.dataBase64 ? { dataBase64: a.dataBase64 } : {}),
        }));
      clearComposer();
      setAttachments([]);
      onOutbound?.();
      enqueueSend({
        text,
        threadKey: threadKey ?? null,
        chatId: chatId ?? null,
        ...(media.length > 0 ? { media } : {}),
      });
      return;
    }

    const optimisticId = `tmp-${Date.now()}`;
    appendLive({
      id: optimisticId,
      clientKey: optimisticId,
      direction: "INBOUND",
      text,
      ageLabel: "now",
      sender: { kind: "visitor", name: "" },
      ...(optimisticMedia.length > 0 ? { media: optimisticMedia } : {}),
      // Optimistic SENT — the next history refetch will swap the
      // tmp row with the real one (DELIVERED). SSE read-receipts
      // for the real id then upgrade it to READ.
      deliveryStatus: "SENT",
    });
    clearComposer();
    setAttachments([]);
    // SEND boundary — which thread does this message target?
    try {
      const res = await send({
        variables: {
          input: {
            ...(text ? { text } : {}),
            ...(mediaIds.length > 0 ? { mediaIds } : {}),
            // Topic-thread key (multi-topic threads). Omitted
            // for the main/anchor thread; a fresh key CREATES the thread and
            // the ack returns its chatId (adopted below).
            ...slotRouting(slotSend()),
          },
        },
      });
      onOutbound?.();
      const ack = res.data?.sendVisitorMessage;
      // A new-question thread had no chatId until this first send created it —
      // surface the ack's chatId so the shell can key history / read / the
      // thread list on it.
      if (ack?.chatId && ack.chatId !== chatId) {
        onChatIdResolved?.(ack.chatId);
      }
      // Reconcile the optimistic tmp-id with the BE's real
      // `messageId`. Keeps the bubble visible the whole time;
      // dedupe against `historyData` now works because both lists
      // refer to the same id.
      const realId = ack?.messageId;
      if (realId) {
        replaceLiveMessage?.(optimisticId, {
          id: realId,
          direction: "INBOUND",
          text,
          ageLabel: "now",
          sender: { kind: "visitor", name: "" },
          ...(optimisticMedia.length > 0 ? { media: optimisticMedia } : {}),
          deliveryStatus: "DELIVERED",
        });
      }
      // No refetch. The previous version called
      //   `refetchHistory({ chatId, limit: 50 })`
      // after every send so Apollo's cache picked up the canonical
      // row. That fired a full chat-history network round-trip on
      // every keystroke-bound send — visibly slowing the bubble
      // appear/settle UX. The combo already in place is enough:
      //   1. `appendLiveMessage(...)` adds the optimistic row to
      //      the shell-owned `liveMessages` array.
      //   2. `replaceLiveMessage(...)` swaps the tmp id for the
      //      BE-canonical id, so dedupe against the cached history
      //      works on the next mount.
      //   3. `liveMessages` lives on the widget shell, not the
      //      Messages tab, so it survives Help / News detours
      //      without a refetch.
      //   4. SSE delivery / read receipts upgrade `deliveryStatus`
      //      on the same in-memory row.
      // The cached `visitorChatHistory` *will* be a beat stale until
      // the next natural refetch (tab focus, app reopen, manual
      // pull-to-refresh), but the UI never displays the cache alone
      // — it always merges with `liveMessages`. So the visitor
      // never sees the stale state.
    } catch {}
  };
  // One send at a time: a held / repeated Enter while the prechat submit is in flight must not send
  // the prechat and the message again.
  const handleSend = useSingleFlight(performSend);

  // Slash-command chips send their literal text as a message. Same
  // optimistic + id-reconcile path as `handleSend`, and gated by the
  // SAME prechat check so tapping a command can't bypass required
  // details (validates + submits the prechat first, aborts if invalid).
  const sendCommand = async (cmd: string) => {
    setCommandsOpen(false);
    if (!(await ensurePrechat())) return;
    if (isDummy) {
      sendDummyExchange(cmd);
      return;
    }
    const optimisticId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    appendLive({
      id: optimisticId,
      clientKey: optimisticId,
      direction: "INBOUND",
      text: cmd,
      ageLabel: "now",
      sender: { kind: "visitor", name: "" },
      deliveryStatus: "SENT",
    });
    onOutbound?.();
    try {
      const res = await send({
        variables: { input: { text: cmd, ...(threadKey ? { threadKey } : {}) } },
      });
      const ack = res.data?.sendVisitorMessage;
      const realId = ack?.messageId;
      if (realId) {
        replaceLiveMessage?.(optimisticId, {
          id: realId,
          direction: "INBOUND",
          text: cmd,
          ageLabel: "now",
          sender: { kind: "visitor", name: "" },
          deliveryStatus: "DELIVERED",
        });
      }
    } catch {}
  };

  // Disable send while ANY attachment is still uploading — otherwise a tap
  // would fire with `mediaIds=[]` and silently drop the file.
  const uploadingAttachment = attachments.some((a) => a.mediaId === null);

  // Operator-configured welcome messages — paint at the top of the
  // thread before any real turn. Each entry is rendered by `kind`:
  //   - TEXT   → normal bot bubble (AI sender). Uses the configured
  //              `aiAssistantAvatar` if set, otherwise hides the
  //              avatar slot entirely.
  //   - HINT   → dim italic affordance pill — sits centered, no
  //              avatar. Reads as a "tip" not a "message".
  //   - SYSTEM → centred system row, same style as runtime
  //              MessageType.SYSTEM events.
  // Demo surfaces a translated SYSTEM + HINT welcome run (showcasing those
  // kinds); live mode uses the operator's configured messages. When a demo
  // surface DID supply its own `messenger.messages` (the playground's editable
  // default-messages list), honour those so edits preview live — the bundled
  // translated run is only the fallback for an unconfigured demo embed.
  // Contract v1 (`messenger.messageSlots` present): slots of variants, picked against the
  // audience; otherwise the legacy flat list exactly as before.
  // A local thread whose slot left the config (snapshot) still renders its header.
  const localKeyId = localSlotIdOfThreadKey(threadKey);
  const slotsActive =
    messenger?.messageSlots !== undefined || (!!localKeyId && !!localSlots.snapshotOf(localKeyId));
  const {
    welcomes: slotWelcomes,
    held: welcomeHeld,
    slotPick,
    welcomePick,
    localHeader,
    seedTexts,
  } = useWelcomeContent({
    publicSlots: messenger?.messageSlots ?? NO_SLOTS,
    active: slotsActive,
    isOpen,
    threadKey,
    welcomeSlotId,
    token,
  });
  const welcomeMessages: WelcomeEntry[] = slotsActive
    ? slotWelcomes.map((w) => ({
        kind: w.kind,
        text: w.variant.text,
        slotId: w.slotId,
        variantId: w.variant.id,
        buttons: w.variant.buttons,
      }))
    : isDummy && !(messenger?.messages && messenger.messages.length > 0)
      ? getMockWelcomeMessages(tDemo)
      : (messenger?.messages ?? []);
  // SYSTEM messages are operator status notices the visitor must see
  // regardless of auth — shown ALWAYS, even behind the sign-in gate /
  // prechat. HINT (tip card) is conversational chrome, shown only once
  // the visitor can actually converse. `TEXT` was retired alongside the
  // WEB_VISITOR_VISIT flow trigger — proactive bot greetings now arrive
  // via SSE as runtime messages.
  const systemMessages = welcomeMessages.filter((w) => w.kind === "SYSTEM");
  const hintMessages = welcomeMessages.filter((w) => w.kind === "HINT");
  // MESSAGE welcomes render as real AI chat bubbles at the top of the thread
  // (pinned like note/system). On a NEW thread's first open they reveal after a
  // brief typing indicator.
  slotPickRef.current = slotPick;
  welcomePickRef.current = welcomePick;
  localHeaderRef.current = localHeader;
  const isNewThread = messages.length === 0;
  // A message-slot thread that already has an agent message is seeded: the BE inserted the slot text
  // as its first agent message, so the client-side copy must not show twice.
  // An existing thread (it has a chat id) with nothing loaded yet is waiting for its history, which holds the seed.
  // Demo / preview without a BE has no seed: the client copy stays the first bubble.
  const slotSeeded =
    !isDummy &&
    (slotPick ?? welcomePick) !== null &&
    (messages.some((m) => m.direction === "OUTBOUND") || (!!chatId && messages.length === 0));
  const slotCopies = welcomeMessages.filter((w) => w.kind === "MESSAGE");
  // The BE seed (the slot text as the first agent message) takes the header's place: the header keeps
  // rendering the same element, and the seed is left out of the dated history, so nothing above the
  // date separator ever moves.
  const headerSeed = isDummy ? null : findHeaderSeed(messages, seedTexts);
  headerSeedIdRef.current = headerSeed?.id ?? null;
  // The client copy stands in for the seed only while it is the same text; a seed of another variant
  // (an operator edit, a reload on another page) is rendered as itself.
  const copyIsSeed =
    !!headerSeed && slotCopies.some((c) => plainText(c.text) === plainText(headerSeed.text ?? ""));
  const messageWelcomes = headerSeed
    ? copyIsSeed
      ? slotCopies
      : []
    : slotSeeded
      ? []
      : slotCopies;
  const headerCount = messageWelcomes.length > 0 ? messageWelcomes.length : headerSeed ? 1 : 0;
  // Stable per-thread reveal identity: the main thread stays "main" even once
  // its chatId is lazily adopted from the first send-ack — keying on chatId used
  // to flip "" -> <real id> at adoption, which replayed the typing reveal
  // (bubble already shown -> typing dots -> bubble again). Topic threads use
  // their stable `threadKey`.
  const threadWelcomeKey = welcomeStateKey(threadKey);
  // `useLayoutEffect` (not `useEffect`) so the typing state is set BEFORE the
  // browser paints — otherwise the initial `welcomeTyping=false` render flashes
  // the welcome bubble for a frame, then the effect flips to typing (visible
  // "bubble -> dots -> bubble" blink on first open).
  useLayoutEffect(() => {
    // No welcome yet (config still loading) → stay UNDECIDED (`null`, render
    // nothing) rather than `false`, so a later config load doesn't briefly paint
    // the bubble before flipping to the typing reveal.
    if (headerCount === 0 && !welcomeHeld) {
      setWelcomeTyping(null);
      return;
    }
    // Not a fresh thread, or the reveal already played for this thread → show the
    // bubbles directly (no typing pass).
    if (!isNewThread || welcomeShownRef.current.has(threadWelcomeKey)) {
      setWelcomeTyping(false);
      return;
    }
    setWelcomeTyping(true);
    // The beat started when the dots first showed (possibly while the session content was held), so
    // a re-run (held -> picked) finishes the remainder instead of adding another 3s.
    const startedAt = welcomeTypingStartRef.current.get(threadWelcomeKey) ?? Date.now();
    welcomeTypingStartRef.current.set(threadWelcomeKey, startedAt);
    const id = setTimeout(
      () => {
        setWelcomeTyping(false);
        welcomeShownRef.current.add(threadWelcomeKey);
      },
      Math.max(0, 3000 - (Date.now() - startedAt)),
    );
    return () => clearTimeout(id);
  }, [isNewThread, headerCount, threadWelcomeKey, welcomeHeld]);
  // Synthetic AI bubbles for the MESSAGE welcomes — authored by the AI agent
  // (name resolved above), rendered through the normal MessageBubble.
  const welcomeBubbles = useMemo<MockMessage[]>(
    () =>
      messageWelcomes.length === 0 && headerSeed
        ? [{ ...headerSeed, id: "welcome-msg-0" }]
        : messageWelcomes.map((wm, i) => ({
            id: `welcome-msg-${i}`,
            direction: "OUTBOUND",
            text: wm.text,
            ageLabel: "",
            sender: { kind: "ai", name: aiName ?? "" },
            // Quick replies (contract v1): the label is both the chip text and the message sent.
            ...(wm.buttons && wm.buttons.length > 0
              ? { buttons: wm.buttons.map((b) => ({ text: b.label, payload: "" })) }
              : {}),
          })),
    [messageWelcomes, headerSeed, aiName],
  );
  // The operator's intro (SYSTEM / HINT) belongs at the very START of
  // the thread, so only surface it once there's nothing older — an empty
  // thread, the sign-in gate (no history), or after the visitor has
  // scrolled up and loaded back to the first message (`!historyHasMore`).
  // A long thread with older pages still to load hides the intro until
  // the start is reached.
  // Keep the intro pinned once the thread has ANY message of its own, so a
  // brand-new thread's first send (which fires the history query → a brief
  // `historyLoading`) doesn't flash the welcome out and back in. Only hide it
  // while older pages are still to load (`historyHasMore`) or during the very
  // first load of an as-yet-empty thread.
  const showIntro =
    isDummy || showAuthGate || (!historyHasMore && (!historyLoading || messages.length > 0));
  // Display toggles plumbed through for future surfaces. None of
  // the three are wired to actual content yet — `showRelatedNews`
  // / `showRelatedHelpArticles` / `showReactionCounts` are reserved
  // for upcoming "related items" tiles + aggregate reaction strip
  // under bot bubbles. When those land, gate them on these flags.
  void messenger?.showRelatedNews;
  void messenger?.showRelatedHelpArticles;
  void messenger?.showReactionCounts;

  // Welcome quick replies are one-shot: they hide after a click or the visitor's first message in
  // this thread (contract v1, `welcomeButtonClick`).
  const [welcomeButtonsUsed, setWelcomeButtonsUsed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const welcomeButtonsActive =
    !welcomeButtonsUsed.has(threadWelcomeKey) && !messages.some((m) => m.direction === "INBOUND");
  const handleWelcomeButton = (wm: WelcomeEntry, btn: MockMessageButton) => {
    setWelcomeButtonsUsed((prev) => new Set(prev).add(threadWelcomeKey));
    const button = wm.buttons?.find((b) => b.label === btn.text);
    if (wm.slotId && wm.variantId && button) {
      const ids = { slotId: wm.slotId, variantId: wm.variantId, buttonId: button.id };
      trackButtonClick({ source: "WELCOME_MESSAGE", ...ids });
      env.onMessageAction?.({ ...ids, actionType: "QUICK_REPLY", source: "WELCOME" });
    }
    // Same path as a flow quick reply, minus the flow `value`: a plain visitor message.
    handleQuickReply(`welcome-${wm.slotId ?? ""}`, { text: btn.text, payload: "" });
  };

  // A quick reply tapped on the launcher message: this thread sends the label as its first message.
  useInitialSlotReply({
    text: initialVisitorMessage,
    enabled: !isDummy && !!enqueueSend && (slotPick !== null || localHeader !== null),
    send: (text) => {
      setWelcomeButtonsUsed((prev) => new Set(prev).add(threadWelcomeKey));
      onOutbound?.();
      enqueueSend?.({ text, threadKey: threadKey ?? null, chatId: chatId ?? null });
    },
    onSent: onInitialVisitorMessageSent,
  });

  // Interleave date separators + the unread divider into the thread.
  // Memoised for the same reason as `messages` above — without this,
  // every keystroke in the composer ran the full row build (one more
  // O(n) walk with date-key bookkeeping). `t` from next-intl is
  // stable across renders, so referencing it directly is fine.
  const messageRows = useMemo(
    () =>
      buildMessageRows(headerSeed ? messages.filter((m) => m.id !== headerSeed.id) : messages, {
        locale,
        todayLabel: t("today"),
        yesterdayLabel: t("yesterday"),
        unreadAnchorId,
      }),
    [messages, headerSeed, locale, t, unreadAnchorId],
  );

  // Stable identities for the memoised list (PF-01): the handlers above are
  // fresh closures each render, which would re-render every row.
  const onQuickReply = useStableCallback(handleQuickReply) as (
    messageId: string,
    button: MockMessageButton,
  ) => void;
  const onOpenLightbox = useStableCallback(openLightbox) as typeof openLightbox;
  const onInternalLinkStable = useStableCallback(onInternalLink) as NonNullable<
    typeof onInternalLink
  >;
  const onRetryStable = useStableCallback((id: string) => onRetryOutbox?.(id)) as (
    id: string,
  ) => void;

  // Smooth-scroll the thread to the newest message (the scroll-down button).
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <ScrollArea className="flex-1" viewportRef={scrollRef}>
          <div className="flex flex-col gap-3 px-4 pt-4 pb-3">
            {/* SYSTEM messages — ALWAYS shown, even behind the sign-in gate /
              prechat. They're operator status notices the visitor needs
              regardless of whether they can converse yet. */}
            {showIntro && systemMessages.length > 0 && (
              <CrossFade
                enabled={messenger?.messageSlots !== undefined}
                id={slotWelcomes
                  .filter((w) => w.kind === "SYSTEM")
                  .map((w) => w.variant.id)
                  .join("|")}
              >
                <div className="flex flex-col gap-2">
                  {systemMessages.map((wm) => (
                    <WelcomeMessage key={`${wm.kind}:${wm.text}`} kind={wm.kind} text={wm.text} />
                  ))}
                </div>
              </CrossFade>
            )}
            {/* MESSAGE welcome — real AI chat bubbles (the greeting). On a NEW
              thread's first open, a typing indicator shows for ~3s, then the
              bubbles reveal; otherwise they render directly. */}
            {showIntro && (headerCount > 0 || welcomeHeld) && welcomeTyping !== null && (
              <AnimatePresence mode="wait" initial={false}>
                {welcomeTyping || welcomeHeld ? (
                  <TypingBubble
                    key="welcome-typing"
                    actorName={aiName ?? ""}
                    actorKind="ai"
                    aiAssistantName={aiName}
                    aiAssistantAvatarUrl={messenger?.aiAssistantAvatar ?? null}
                    organizationLogoUrl={organizationLogoUrl}
                    organizationName={organizationName}
                  />
                ) : (
                  <motion.div
                    key="welcome-msgs"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, ease: "easeOut" }}
                    className="flex flex-col gap-3"
                  >
                    {welcomeBubbles.map((wm, i) => (
                      <MessageBubble
                        key={wm.id}
                        message={wm}
                        buttonsActive={welcomeButtonsActive && !headerSeed}
                        onButtonClick={(btn) => handleWelcomeButton(messageWelcomes[i], btn)}
                        aiAssistantAvatarUrl={messenger?.aiAssistantAvatar ?? null}
                        aiAssistantName={aiName}
                        organizationLogoUrl={organizationLogoUrl}
                        organizationName={organizationName}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
            {/* HINT (tip card): part of the operator's configured intro — shown
              at the START of the thread (empty thread / sign-in gate / once
              scrolled back to the first message), like SYSTEM. */}
            {showIntro && hintMessages.length > 0 && (
              <div className="flex flex-col gap-2">
                {hintMessages.map((wm) => (
                  <WelcomeMessage key={`${wm.kind}:${wm.text}`} kind={wm.kind} text={wm.text} />
                ))}
              </div>
            )}
            {/* Conversation. Suppressed entirely while the sign-in gate is up
              — there's no thread to render until auth clears. */}
            {!showAuthGate && (
              <>
                {historyLoading && messages.length === 0 && (
                  // Chat-history skeleton — three alternating bubbles
                  // (operator + visitor) so the layout doesn't flicker once
                  // the real history lands.
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-2">
                      <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
                      <Skeleton className="h-10 w-3/4 rounded-wx-lg" />
                    </div>
                    <div className="flex flex-row-reverse items-start">
                      <Skeleton className="h-8 w-2/3 rounded-wx-lg" />
                    </div>
                    <div className="flex items-start gap-2">
                      <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
                      <Skeleton className="h-12 w-4/5 rounded-wx-lg" />
                    </div>
                  </div>
                )}
                <MessageList
                  rows={messageRows}
                  readMessageIds={readMessageIds}
                  isDummy={isDummy}
                  operatorAvatarSrc={operatorAvatars?.[0]?.src}
                  activeButtonMessageId={activeButtonMessageId}
                  unreadLabel={t("unreadMessages")}
                  unreadDividerRef={unreadDividerRef}
                  organizationLogoUrl={organizationLogoUrl}
                  organizationName={organizationName}
                  aiAssistantAvatarUrl={messenger?.aiAssistantAvatar ?? null}
                  aiAssistantName={aiName}
                  onQuickReply={onQuickReply}
                  onOpenLightbox={onOpenLightbox}
                  onInternalLink={onInternalLink ? onInternalLinkStable : undefined}
                  onRetry={onRetryOutbox ? onRetryStable : undefined}
                />
              </>
            )}
            {!showAuthGate && shownTyping && (
              <TypingBubble
                actorName={shownTyping.name}
                actorKind={shownTyping.kind}
                actorAvatarUrl={shownTyping.avatar}
                actorPhase={shownTyping.phase}
                aiAssistantName={aiName}
                aiAssistantAvatarUrl={messenger?.aiAssistantAvatar ?? null}
                organizationLogoUrl={organizationLogoUrl}
                organizationName={organizationName}
              />
            )}
          </div>
        </ScrollArea>
        {/* Scroll-to-newest button — slides up from below when the visitor has
            scrolled up, slides back down as they reach the bottom. */}
        <AnimatePresence>
          {showScrollDown && (
            <motion.button
              type="button"
              onClick={scrollToBottom}
              aria-label={t("scrollToLatest")}
              // `x: "-50%"` keeps the button centred THROUGH the motion
              // transform — the old Tailwind `-translate-x-1/2` would be
              // clobbered by the inline transform that `whileHover` scale sets,
              // knocking the button off-centre on hover.
              initial={{ opacity: 0, y: 12, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 12, x: "-50%" }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.94 }}
              className="absolute bottom-3 left-1/2 z-10 flex size-9 items-center justify-center rounded-full border border-wx-border bg-wx-bg text-wx-fg-muted shadow-md transition-colors hover:text-wx-fg"
            >
              <ChevronDown className="size-5" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <div className="relative bg-wx-bg px-3 pt-2 pb-3">
        {assignmentStatus === "pending" && !isClosed && !isBlocked && (
          <div className="mb-2 flex justify-center">
            <span className="rounded-wx-sm bg-wx-bg-elevated px-3 py-1 text-center text-[11px] text-wx-fg-muted">
              {/* One line: the ETA REPLACES the generic "connecting…" copy when
                  the BE gave us an estimate (offline → "team back at …"); no
                  estimate → the plain "please wait" fallback. */}
              {(assignmentEstimate && assignmentEtaText(assignmentEstimate, t, locale)) ||
                t("connectingOperator")}
            </span>
          </div>
        )}
        <EmojiPicker
          open={emojiOpen}
          onClose={() => setEmojiOpen(false)}
          onSelect={insertEmoji}
          anchorRef={emojiAnchorRef}
        />
        <Suspense fallback={null}>
          <GiphyPicker
            open={giphyOpen}
            onClose={() => setGiphyOpen(false)}
            fetchPage={giphyFetchPage}
            onPick={handleGiphyPick}
            anchorRef={giphyAnchorRef}
          />
        </Suspense>
        {uploadError && <p className="mb-2 text-xs text-wx-danger">{uploadError}</p>}

        <AnimatePresence mode="wait" initial={false}>
          {isBlocked ? (
            // BLOCKED — no composer. A thread-only block still offers "start a
            // new conversation"; a global (contact) block hides it too.
            <motion.div
              key="blocked"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-center gap-3 rounded-wx-lg border border-wx-border bg-wx-bg p-4"
            >
              <p className="text-center text-sm text-wx-fg-muted">
                {isGloballyBlocked ? t("visitorBlocked") : t("conversationBlocked")}
              </p>
              {!isGloballyBlocked && onStartNewConversation && (
                <Button type="button" onClick={onStartNewConversation} className="w-full">
                  {t("startNewConversation")}
                </Button>
              )}
            </motion.div>
          ) : isClosed ? (
            // Conversation is CLOSED — no composer. The visitor can only start
            // a fresh conversation from here.
            <motion.div
              key="closed"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-center gap-2 rounded-wx-lg border border-wx-border bg-wx-bg p-4"
            >
              <p className="text-center font-semibold text-sm text-wx-fg">
                {t("conversationClosedTitle")}
              </p>
              <p className="text-center text-xs text-wx-fg-muted">{t("conversationClosedHint")}</p>
              {onStartNewConversation && (
                <Button type="button" onClick={onStartNewConversation} className="mt-1 w-full">
                  {t("startNewConversation")}
                </Button>
              )}
            </motion.div>
          ) : showAuthGate ? (
            // Operator requires a verified identity — replace the whole
            // composer with the sign-in panel until the visitor
            // authenticates (then `verified` flips and the composer
            // returns). The handshake runs in the shell off the resulting
            // identity, so there's nothing to wire here beyond the token.
            <motion.div
              key="auth-gate"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-center gap-2 rounded-wx-lg border border-wx-border bg-wx-bg p-4"
            >
              <p className="text-center text-sm text-wx-fg-muted">{t("signInToChat")}</p>
              {/* Full-width button column. Both methods stretch to fill it:
                  the passkey button natively, and the Google (GIS) button via
                  the `[data-wx-gis]` override in globals.css that lifts GIS's
                  hard 400px cap. */}
              <div className="flex w-full flex-col gap-2">
                {mockAuth ? (
                  // PREVIEW — no real auth backend; mock buttons fake-complete
                  // so the operator sees the gate → composer transition.
                  <>
                    {security?.google?.enabled && (
                      <button
                        type="button"
                        onClick={() => setDemoAuthed(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-wx border border-wx-border bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-fg transition-colors hover:bg-wx-bg-elevated-2"
                      >
                        {t("googleSignInDemo")}
                      </button>
                    )}
                    {security?.passkey?.enabled && (
                      <button
                        type="button"
                        onClick={() => setDemoAuthed(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-wx border border-wx-border bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-fg transition-colors hover:bg-wx-bg-elevated-2"
                      >
                        {t("passkeySignIn")}
                      </button>
                    )}
                    {!security?.google?.enabled && !security?.passkey?.enabled && (
                      <p className="text-center text-xs text-wx-fg-subtle">
                        {t("signInUnavailable")}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {/* Google: direct GIS integration. Widget loads
                        `accounts.google.com/gsi/client`, renders the
                        native button, and pipes the resulting id_token
                        through `onGoogleCredential` → `setWidgetIdentity({
                        googleIdToken })` in the shell → the identified
                        handshake → BE audience check. */}
                    {googleSignIn && (
                      <GoogleSignInButton
                        clientId={googleSignIn}
                        onCredential={(idToken) => onGoogleCredential?.(idToken)}
                      />
                    )}
                    {/* Passkey: in-widget WebAuthn ceremony (no host
                        bridge). */}
                    {passkeySignIn && <PasskeySignInButton onSession={onPasskeySession} />}
                    {!googleSignIn && !passkeySignIn && (
                      // requireAuth on, but no in-widget method enabled — the
                      // host is expected to identify the visitor (props / Wexio).
                      <p className="text-center text-xs text-wx-fg-subtle">
                        {t("signInUnavailable")}
                      </p>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          ) : recording ? (
            // Fallback matches the recorder bar's height so the composer
            // area doesn't jump while the wavesurfer chunk streams in.
            <Suspense
              key="recorder"
              fallback={
                <div className="h-13 w-full animate-pulse rounded-wx-lg bg-wx-bg-elevated" />
              }
            >
              <VoiceRecorderBar
                onComplete={handleRecordingComplete}
                onCancel={() => setRecording(false)}
                onError={handleRecordingError}
              />
            </Suspense>
          ) : (
            <motion.div
              key="composer"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className={cn(
                "flex flex-col rounded-wx-lg border border-wx-border bg-wx-bg",
                "focus-within:border-wx-primary focus-within:ring-2 focus-within:ring-wx-primary/20",
                "transition-colors",
              )}
            >
              {/* Staged attachments — INSIDE the composer's bordered card, at
                  the top, so the row reads as part of the editor (one card)
                  instead of a detached strip floating above it. */}
              {attachments.length > 0 && (
                <div className="px-3 pt-3 pb-1">
                  <AttachmentPreview
                    attachments={attachments}
                    onRemove={removeAttachment}
                    onOpenLightbox={openLightbox}
                  />
                </div>
              )}
              {/* Prechat — compact fields ABOVE the editor. The visitor
                  fills them alongside their first message; the single
                  send (below) submits the prechat then the message. They
                  disappear once submitted (or when a known-user identity
                  already covers the required fields). */}
              {showPrechat && prechatForm && (
                <>
                  <PrechatComposerFields
                    form={prechatForm}
                    values={formValues}
                    locked={known.locked}
                    errors={prechatErrors}
                    onChange={setPrechatField}
                    disabled={submittingPrechat}
                  />
                  {prechatSubmitError && (
                    <p className="px-3 pt-1.5 text-[11px] text-wx-danger">{prechatSubmitError}</p>
                  )}
                </>
              )}
              <RichTextEditor
                ref={editorHandleRef}
                placeholder={t("sendCta")}
                ariaLabel={t("sendCta")}
                initialHtml={draftStore.get().html}
                onChange={({ html, text }) => draftStore.set(text, html)}
                onTyping={fireTypingSignal}
                onEnter={handleSend}
                maxChars={MAX_COMPOSER_CHARS}
              />
              <div className="flex items-center justify-between px-1.5 pt-0.5 pb-1.5">
                <div className="flex items-center gap-0.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={Array.from(ALLOWED_UPLOAD_MIMES).join(",")}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileChosen(file);
                      e.target.value = "";
                    }}
                  />
                  <motion.button
                    type="button"
                    aria-label={t("attach")}
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-wx-fg-muted transition-colors hover:bg-wx-bg-elevated hover:text-wx-fg"
                  >
                    <Paperclip size={14} />
                  </motion.button>
                  <motion.button
                    ref={emojiAnchorRef}
                    type="button"
                    aria-label={t("emoji")}
                    aria-expanded={emojiOpen}
                    onClick={() => {
                      setGiphyOpen(false);
                      setEmojiOpen((v) => !v);
                    }}
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                      emojiOpen
                        ? "bg-wx-bg-elevated text-wx-fg"
                        : "text-wx-fg-muted hover:bg-wx-bg-elevated hover:text-wx-fg",
                    )}
                  >
                    <Smile size={14} />
                  </motion.button>
                  {/* GIF / Sticker (Giphy) — supported by default. Opens the
                      picker after the emoji button; closes emoji/commands. If
                      the BE proxy is unavailable it returns a clean error and
                      the picker shows its error state (no client feature gate). */}
                  <motion.button
                    ref={giphyAnchorRef}
                    type="button"
                    aria-label={t("giphy.title")}
                    aria-expanded={giphyOpen}
                    onClick={() => {
                      setEmojiOpen(false);
                      setCommandsOpen(false);
                      setGiphyOpen((v) => !v);
                    }}
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                      giphyOpen
                        ? "bg-wx-bg-elevated text-wx-fg"
                        : "text-wx-fg-muted hover:bg-wx-bg-elevated hover:text-wx-fg",
                    )}
                  >
                    {/* "GIF" text badge reads clearer than a generic image icon
                        for a GIF/sticker picker (a common messenger convention). */}
                    <span className="font-bold text-[10px] leading-none tracking-tight">GIF</span>
                  </motion.button>
                  {/* Commands chip dropdown — only rendered when the
                      operator has configured at least one command. A
                      tap opens a popover with the configured commands;
                      picking one sends the literal text as an inbound
                      visitor message (same path the typed-send uses). */}
                  {(messenger?.commands?.length ?? 0) > 0 && (
                    <div className="relative">
                      <motion.button
                        ref={commandsAnchorRef}
                        type="button"
                        aria-label={t("commands")}
                        aria-expanded={commandsOpen}
                        onClick={() => {
                          setGiphyOpen(false);
                          setCommandsOpen((v) => !v);
                        }}
                        whileHover={{ scale: 1.12 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        }}
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                          commandsOpen
                            ? "bg-wx-bg-elevated text-wx-fg"
                            : "text-wx-fg-muted hover:bg-wx-bg-elevated hover:text-wx-fg",
                        )}
                      >
                        <Command size={14} />
                      </motion.button>
                      <AnimatePresence>
                        {commandsOpen && (
                          <motion.div
                            data-wx-commands-panel
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            transition={{ duration: 0.12 }}
                            className="absolute bottom-full left-0 z-10 mb-2 w-44 overflow-hidden rounded-lg border border-wx-border bg-wx-bg shadow-lg"
                          >
                            <ul className="flex flex-col py-1">
                              {messenger?.commands.map((cmd) => (
                                <li key={cmd}>
                                  <button
                                    type="button"
                                    onClick={() => sendCommand(cmd)}
                                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-mono text-wx-fg hover:bg-wx-bg-elevated"
                                  >
                                    {cmd}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
                <ComposerSendControls
                  draft={draftStore}
                  hasAttachments={attachments.length > 0}
                  busy={sending || uploadingAttachment}
                  onSend={handleSend}
                  onMic={startRecording}
                  ariaLabels={{
                    send: t("sendCta"),
                    mic: t("record"),
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MediaLightbox
        items={lightbox?.items ?? []}
        initialIndex={lightbox?.index ?? 0}
        open={!!lightbox}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
}
